import React, { useState, useEffect, useRef, useCallback } from "react";
import { AppLayout, useOrderAlerts, orderAlerts } from "./AppLayout";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../lib/api";
import {
  ClipboardList, Clock, ChefHat, CheckCircle2, XCircle, X,
  Printer, CreditCard, RefreshCw, Plus, AlertTriangle,
  Banknote, Smartphone, CreditCard as CardIcon,
  ShoppingCart, Search, Pencil, Minus, User, Phone,
  Receipt, BedDouble, Truck, ChevronDown, ChevronRight, Trash2, SlidersHorizontal,
} from "lucide-react";
import { toast } from "sonner";
import { printHtml, printInvoice, pagePrintCss } from "../../lib/print";

// ── Helpers ──────────────────────────────────────────────────────────────────

function elapsed(createdAt) {
  const diff = Math.floor((Date.now() - new Date(createdAt).getTime()) / 1000);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  return `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m`;
}

function formatTime(iso) {
  return new Date(iso).toLocaleTimeString("en-IN", {
    hour: "2-digit", minute: "2-digit", hour12: true,
  });
}

// The chosen variant + add-on names for an order line. Both the kitchen (KOT) and every
// order view need this — a line stores its item name plus variant_label and addons, and
// without them a "Cold Mezze Platter" ticket can't be made with Almond milk.
function orderLineDetails(it) {
  return [it.variant_label, ...((it.addons || []).map(a => a && a.name))]
    .filter(Boolean).join(" · ");
}

// ── KOT Print ────────────────────────────────────────────────────────────────

function printKOT(order, venueName, widthMm = 80) {
  const w = widthMm === 58 ? 58 : 80;   // venue thermal roll width
  const itemsHtml = order.items.map(it => {
    const details = orderLineDetails(it);
    const code = it.item_code ? `<span style="font-weight:bold">[${it.item_code}]</span> ` : "";
    return `<tr>
      <td style="padding:2px 4px;font-size:13px;vertical-align:top">${it.qty}x</td>
      <td style="padding:2px 4px;font-size:13px;width:100%">${code}${it.name}${details ? `<br><span style="font-size:12px;font-weight:bold">${details}</span>` : ""}${it.note ? `<br><span style="font-size:11px;color:#666">Note: ${it.note}</span>` : ""}</td>
      <td style="padding:2px 4px;font-size:13px;text-align:right;vertical-align:top">₹${(it.line_total || 0).toFixed(0)}</td>
    </tr>`;
  }).join("");

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>KOT #${order.order_number}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Courier New', monospace; width: ${w}mm; padding: 8px; }
        .header { text-align: center; border-bottom: 1px dashed #000; padding-bottom: 6px; margin-bottom: 6px; }
        .venue { font-size: 14px; font-weight: bold; }
        .order-num { font-size: 20px; font-weight: bold; margin: 4px 0; }
        .meta { font-size: 11px; color: #333; }
        table { width: 100%; border-collapse: collapse; margin: 6px 0; }
        .divider { border-top: 1px dashed #000; margin: 6px 0; }
        .total-row { font-weight: bold; font-size: 13px; }
        .footer { text-align: center; font-size: 10px; margin-top: 8px; color: #666; }
        .note { font-size: 11px; background: #f5f5f5; padding: 4px; margin: 4px 0; }
        ${pagePrintCss(w)}
      </style>
    </head>
    <body>
      <div class="header">
        <div class="venue">${venueName}</div>
        <div class="order-num">ORDER #${order.order_number}</div>
        <div class="meta">
          ${order.order_type === "room" ? `Room ${order.room_number}` : order.order_type === "dine_in" ? `Table ${order.table_number}` : "Takeaway"} &nbsp;|&nbsp;
          ${formatTime(order.created_at)}
        </div>
        ${order.vehicle_number ? `<div class="meta">Vehicle: ${order.vehicle_number}</div>` : ""}
        <div class="meta">${order.customer_name}${order.customer_phone ? ` · ${order.customer_phone}` : ""}</div>
      </div>
      <table>
        ${itemsHtml}
      </table>
      <div class="divider"></div>
      <table>
        ${order.gst_rate > 0 ? `
        <tr><td style="font-size:12px">Subtotal</td><td style="text-align:right;font-size:12px">₹${(order.subtotal || 0).toFixed(2)}</td></tr>
        <tr><td style="font-size:12px">GST (${order.gst_rate}%)</td><td style="text-align:right;font-size:12px">₹${(order.gst_amount || 0).toFixed(2)}</td></tr>
        ` : ""}
        <tr class="total-row"><td>TOTAL</td><td style="text-align:right">₹${(order.total || 0).toFixed(0)}</td></tr>
      </table>
      ${order.special_note ? `<div class="note">⚠ Note: ${order.special_note}</div>` : ""}
      <div class="footer">Powered by TabOn · tabon.app</div>
    </body>
    </html>
  `;
  // Through the shared helper (hidden iframe, never a pop-up): a tablet silently blocks a pop-up
  // opened after an await, and the counter would never learn the docket was lost. meta → the print log.
  return printHtml(html, "order docket", { document: "kot", refType: "order", refId: order.id });
}

// Print the per-order invoice/receipt so staff can carry the docket to the room with the food.
// Same document, same transport as everywhere else; the helper surfaces any failure.
function printDocket(order) {
  return printInvoice(order.id, { meta: { document: "bill", refType: "order", refId: order.id } });
}

// ── Payment Modal ─────────────────────────────────────────────────────────────

function PaymentModal({ order, onClose, onPaid }) {
  const [method, setMethod] = useState("cash");
  const [received, setReceived] = useState("");
  const [reference, setReference] = useState("");
  const [saving, setSaving] = useState(false);

  // --- Loyalty redemption (Prompt R, Commit 3). The counter order already carries customer_phone,
  //     so we preview on open — no phone to collect. Same flow as the dine-in settle dialog. ---
  const [rPreview, setRPreview] = useState(null);     // {redeemable, balance, earned_today, blocked} | null
  const [rPoints, setRPoints] = useState("");
  const [rStage, setRStage] = useState("idle");       // "idle" | "otp" | "verified"
  const [rPendingId, setRPendingId] = useState(null);
  const [rOtp, setROtp] = useState("");
  const [rNet, setRNet] = useState(null);             // server's post-redemption rounded_total
  const [rPointsApplied, setRPointsApplied] = useState(0);
  const [rBusy, setRBusy] = useState(false);
  const [rResends, setRResends] = useState(0);
  const [showOverride, setShowOverride] = useState(false);
  const [overridePin, setOverridePin] = useState("");

  // Validate, show change, and auto-fill against the ROUNDED total the customer actually pays (K-3),
  // never the un-rounded order.total. A verified redemption drops it to the server's net_payable
  // (GST-correct — redemption is pre-tax), so the collected figure follows (Prompt R).
  const baseTotal = order?.bill?.rounded_total != null ? order.bill.rounded_total : Math.round(order?.total || 0);
  const redeemed = rStage === "verified" && rNet != null;
  const redeemable = Number((rPreview || {}).redeemable || 0);
  const total = redeemed ? Number(rNet) : baseTotal;
  const requiresAmount = method === "cash";
  const change = requiresAmount && parseFloat(received) >= total
    ? (parseFloat(received) - total).toFixed(2)
    : null;

  const orderPhone = (order?.customer_phone || "").replace(/\D/g, "");

  // Preview on open — the order's own phone, the order's own rounded_total as the bill.
  useEffect(() => {
    if (orderPhone.length !== 10 || rStage !== "idle") return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post(`/staff/orders/${order.id}/redemption-preview`, { customer_phone: orderPhone });
        if (!cancelled) setRPreview(data);
      } catch { if (!cancelled) setRPreview(null); }   // 404 no customer → silent
    })();
    return () => { cancelled = true; };
  }, [order.id, orderPhone, rStage]);

  const sendOtp = async () => {
    const pts = parseInt(rPoints, 10);
    if (!(pts > 0)) { toast.error("Enter points to redeem"); return; }
    if (pts > redeemable) { toast.error(`Only ${redeemable} points can be redeemed on this bill`); return; }
    setRBusy(true);
    try {
      const { data } = await api.post("/staff/redeem/initiate", { customer_phone: orderPhone, points: pts, order_id: order.id });
      setRPendingId(data.pending_id); setRNet(data.net_payable); setRPointsApplied(pts);
      setRStage("otp"); setRResends(0);
      toast.success("OTP sent to the customer");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not send OTP"); }
    finally { setRBusy(false); }
  };

  const resendOtp = async () => {
    setRBusy(true);
    try {
      await api.post("/staff/redeem/resend-otp", { pending_id: rPendingId });
      setRResends((n) => n + 1); toast.success("OTP resent");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not resend"); }
    finally { setRBusy(false); }
  };

  const verifyOtp = async () => {
    const code = (rOtp || "").replace(/\D/g, "");
    if (code.length < 4) { toast.error("Enter the OTP"); return; }
    setRBusy(true);
    try {
      await api.post("/staff/redeem/verify-otp", { pending_id: rPendingId, otp_code: code });
      setRStage("verified"); toast.success(`Redeemed ${rPointsApplied} points`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Incorrect OTP"); }
    finally { setRBusy(false); }
  };

  const overrideOtp = async () => {
    const pin = (overridePin || "").replace(/\D/g, "");
    if (pin.length !== 4) { toast.error("Enter the 4-digit override PIN"); return; }
    setRBusy(true);
    try {
      await api.post("/staff/redeem/manager-override", { pending_id: rPendingId, manager_pin: pin });
      setRStage("verified"); setShowOverride(false);
      toast.success(`Redeemed ${rPointsApplied} points (manager override)`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Override failed"); }
    finally { setRBusy(false); }
  };

  const cancelRedeem = () => {
    // Abandon the pending — the cleanup job reverses an unconsumed verified redemption (no second
    // reversal, per spec). Reset the UI so the cashier can collect the full total.
    setRStage("idle"); setRPendingId(null); setROtp(""); setRNet(null); setRPointsApplied(0);
    setRResends(0); setShowOverride(false); setOverridePin(""); setRPoints("");
  };

  // Clear reference when switching payment method
  const handleMethodChange = (key) => {
    setMethod(key);
    setReference("");
  };

  const refLabel = {
    upi: "UPI Transaction ID (UTR)",
    card: "Approval code / Last 4 digits",
    dineout: "Dineout Booking ID",
    district: "District Booking ID",
    eazydiner: "EazyDiner Booking ID",
  }[method] || "Reference";

  const refPlaceholder = {
    upi: "e.g. 324156789012",
    card: "e.g. AUTH123 or 4242",
    dineout: "e.g. DIN-2026-884521",
    district: "e.g. DST-77421",
    eazydiner: "e.g. ED-19928",
  }[method] || "Reference number";

  const submit = async () => {
    if (!method) return;
    if (requiresAmount && (!received || parseFloat(received) < total)) {
      toast.error("Amount received must be at least ₹" + total.toFixed(0));
      return;
    }
    setSaving(true);
    try {
      await api.post(`/orders/${order.id}/payment`, {
        payment_method: method,
        amount_received: requiresAmount ? parseFloat(received) : total,
        ...(reference.trim() ? { payment_reference: reference.trim() } : {}),
        ...(redeemed ? { redemption_pending_id: rPendingId } : {}),
      });
      toast.success(`Order #${order.order_number} — payment recorded`);
      onPaid();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Payment failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl w-full max-w-sm p-6">
        <h3 className="text-lg font-bold text-white mb-1">Record Payment</h3>
        <p className="text-sm text-[#A0A0A0] mb-5">
          Order #{order.order_number} · {order.customer_name} · <span className="text-white font-semibold">₹{total.toFixed(0)}</span>
        </p>

        {/* Loyalty redemption (Prompt R, Commit 3) — previewed on open from the order's own phone */}
        {rStage === "verified" && (
          <div className="flex items-center justify-between bg-[#9D00FF]/10 border border-[#9D00FF]/40 rounded-lg px-3 py-2 mb-4">
            <span className="text-sm text-[#D78CFF]">Loyalty discount <b>−₹{rPointsApplied}</b> · new total ₹{total.toFixed(0)}</span>
            <button type="button" onClick={cancelRedeem} className="text-xs text-[#A0A0A0] hover:text-white">Remove</button>
          </div>
        )}
        {rStage === "idle" && rPreview && redeemable > 0 && (
          <div className="bg-[#0A0A0A] border border-[#9D00FF]/30 rounded-lg p-3 mb-4">
            <div className="text-xs text-[#A0A0A0] mb-1.5">🏆 {rPreview.balance} points · up to <b className="text-[#9D00FF]">{redeemable}</b> redeemable on this bill</div>
            <div className="flex gap-2">
              <input type="number" min={1} max={redeemable} value={rPoints} onChange={(e) => setRPoints(e.target.value)}
                placeholder="Points" style={{ fontSize: 16 }} className="w-24 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-[#9D00FF] focus:outline-none" />
              <button type="button" onClick={sendOtp} disabled={rBusy} className="flex-1 bg-[#9D00FF]/80 hover:bg-[#9D00FF] disabled:opacity-40 text-white text-sm font-semibold rounded-lg px-3">{rBusy ? "…" : "Send OTP"}</button>
            </div>
          </div>
        )}
        {rStage === "idle" && rPreview && redeemable === 0 && rPreview.blocked && (
          <div className="text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 mb-4">{rPreview.blocked}</div>
        )}
        {rStage === "otp" && (
          <div className="bg-[#0A0A0A] border border-[#9D00FF]/30 rounded-lg p-3 mb-4">
            <div className="text-xs text-[#A0A0A0] mb-1.5">Enter the OTP sent to the customer to redeem {rPointsApplied} points</div>
            <div className="flex gap-2">
              <input type="tel" inputMode="numeric" value={rOtp} onChange={(e) => setROtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="______" style={{ fontSize: 16 }} className="w-28 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-white text-sm tracking-[0.3em] focus:border-[#9D00FF] focus:outline-none" />
              <button type="button" onClick={verifyOtp} disabled={rBusy} className="flex-1 bg-[#9D00FF]/80 hover:bg-[#9D00FF] disabled:opacity-40 text-white text-sm font-semibold rounded-lg px-3">{rBusy ? "…" : "Verify"}</button>
            </div>
            <div className="flex items-center justify-between mt-2">
              <button type="button" onClick={resendOtp} disabled={rBusy || rResends >= 3} className="text-[11px] text-[#9D00FF] disabled:opacity-40">Resend OTP{rResends >= 3 ? " (max reached)" : ""}</button>
              <button type="button" onClick={cancelRedeem} className="text-[11px] text-[#A0A0A0]">Cancel</button>
            </div>
            {showOverride ? (
              <div className="flex gap-2 mt-2">
                <input type="password" inputMode="numeric" value={overridePin} onChange={(e) => setOverridePin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  placeholder="Manager PIN" style={{ fontSize: 16 }} className="w-32 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-white text-sm tracking-[0.3em] focus:border-[#9D00FF] focus:outline-none" />
                <button type="button" onClick={overrideOtp} disabled={rBusy} className="flex-1 bg-white/5 border border-white/10 disabled:opacity-40 text-white text-sm font-semibold rounded-lg px-3">{rBusy ? "…" : "Override"}</button>
              </div>
            ) : (
              <button type="button" onClick={() => setShowOverride(true)} className="text-[11px] text-[#A0A0A0] mt-2 block">Customer didn't receive the OTP?</button>
            )}
          </div>
        )}

        {/* Payment method */}
        <p className="text-xs uppercase tracking-wider text-[#A0A0A0] mb-2">Payment method</p>
        <div className="grid grid-cols-3 gap-2 mb-4">
          {[
            { key: "cash",      label: "Cash",      icon: Banknote },
            { key: "upi",       label: "UPI",       icon: Smartphone },
            { key: "card",      label: "Card",      icon: CardIcon },
            { key: "dineout",   label: "Dineout",   icon: CreditCard },
            { key: "district",  label: "District",  icon: CreditCard },
            { key: "eazydiner", label: "EazyDiner", icon: CreditCard },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => handleMethodChange(key)}
              className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border text-xs font-semibold transition ${
                method === key
                  ? "bg-[#9D00FF]/20 border-[#9D00FF] text-white"
                  : "border-white/10 text-[#A0A0A0] hover:border-white/30"
              }`}
            >
              <Icon size={18} />
              {label}
            </button>
          ))}
        </div>

        {/* Cash: amount received */}
        {method === "cash" && (
          <div className="mb-4">
            <label className="text-xs uppercase tracking-wider text-[#A0A0A0] block mb-2">
              Amount received
            </label>
            <input
              type="number"
              inputMode="numeric"
              value={received}
              onChange={e => setReceived(e.target.value)}
              placeholder={`₹${total.toFixed(0)} or more`}
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white text-lg font-semibold focus:border-[#9D00FF] focus:outline-none"
              style={{ fontSize: 16 }}
              autoFocus
            />
            {change !== null && (
              <div className="mt-2 text-emerald-400 text-sm font-semibold">
                Change to return: ₹{change}
              </div>
            )}
          </div>
        )}

        {/* Non-cash: reference number */}
        {method !== "cash" && (
          <div className="mb-4">
            <label className="text-xs uppercase tracking-wider text-[#A0A0A0] block mb-2">
              {refLabel}
              <span className="text-[#555] normal-case tracking-normal ml-1">(optional)</span>
            </label>
            <input
              type="text"
              value={reference}
              onChange={e => setReference(e.target.value)}
              placeholder={refPlaceholder}
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:border-[#9D00FF] focus:outline-none"
              style={{ fontSize: 16 }}
              autoFocus
            />
          </div>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 rounded-xl border border-white/10 text-[#A0A0A0] text-sm font-semibold hover:border-white/30 transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="flex-1 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-semibold transition"
          >
            {saving ? "Saving…" : "Confirm Payment"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Customisation (variants + add-ons), shared by the counter and the edit modal ─────────────
//
// cartLineKey and applicableGroups MIRROR the same functions in PublicMenu.jsx and the merge in
// the server's _resolve_order_line — same resolution, different styling (the guest markup is not
// imported). Keep them in step: the counter must show exactly what the server will price/enforce.

// Deterministic, order-independent line identity: item | variant | sorted add-on ids. So the
// same add-ons chosen in a different order collapse to ONE line, and two different
// customisations of the same item stay as two lines.
function cartLineKey(itemId, variantId, addonOptionIds) {
  return `${itemId}|${variantId || ""}|${[...(addonOptionIds || [])].sort().join(",")}`;
}

// Add-on groups applicable to ONE variant selection: item-level links plus the chosen variant's
// own, deduped with the item-level rule winning, required (min>=1) first. A variant-only group
// therefore appears only when that variant is selected.
function applicableGroups(item, variant, addonCatalog) {
  if (!addonCatalog || !item) return [];
  const links = [...(item.addon_groups || []), ...(variant ? (variant.addon_groups || []) : [])];
  const byId = new Map();
  for (const lk of links) {
    const gid = lk && lk.group_id;
    if (!gid || byId.has(gid)) continue;
    const g = addonCatalog[gid];
    if (!g || !Array.isArray(g.items) || g.items.length === 0) continue;
    byId.set(gid, { id: gid, name: g.name, display_order: g.display_order,
                    items: g.items, min: lk.min, max: lk.max });
  }
  return [...byId.values()].sort((a, b) => {
    const ar = Number(a.min) >= 1 ? 0 : 1;
    const br = Number(b.min) >= 1 ? 0 : 1;
    if (ar !== br) return ar - br;
    return (a.display_order ?? 0) - (b.display_order ?? 0);
  });
}

// One customisation sheet for both the counter (NewOrderModal) and the edit modal. Variant
// selection (nothing preselected), one block per applicable add-on group with its min/max
// enforced in the UI, a live line price, and a Confirm that stays disabled until every required
// choice is made. onConfirm hands back a resolved line; the server re-prices authoritatively.
function CustomiseSheet({ item, addonCatalog, onCancel, onConfirm }) {
  const variants = Array.isArray(item.variants) ? item.variants : [];
  const hasVariants = Boolean(item.has_variants) && variants.length > 0;
  const [variantId, setVariantId] = useState(null);          // nothing preselected
  const [selected, setSelected] = useState(() => new Set()); // chosen add-on option ids
  const [qty, setQty] = useState(1);

  const variant = hasVariants ? (variants.find(v => v.id === variantId) || null) : null;
  const groups = applicableGroups(item, variant, addonCatalog);

  // Changing the variant re-resolves the applicable groups (variants carry their own links), so
  // drop any selection the new set no longer offers — else the server would reject it.
  useEffect(() => {
    const vs = Array.isArray(item.variants) ? item.variants : [];
    const vv = vs.find(v => v.id === variantId) || null;
    const offered = new Set();
    for (const g of applicableGroups(item, vv, addonCatalog)) for (const o of g.items) offered.add(o.id);
    setSelected(prev => {
      const kept = [...prev].filter(id => offered.has(id));
      return kept.length === prev.size ? prev : new Set(kept);
    });
  }, [variantId, item, addonCatalog]);

  const toggle = (group, optionId, active) => {
    if (!active) return;
    const mx = Number(group.max) || 0;
    const mn = Number(group.min) || 0;
    setSelected(prev => {
      const next = new Set(prev);
      if (mx === 1) {
        // Single-choice: selecting replaces the current pick; tapping the current pick clears
        // it only when the group is optional, so a required radio can't be emptied by a mis-tap.
        const wasOnly = next.has(optionId);
        for (const o of group.items) next.delete(o.id);
        if (!(wasOnly && mn === 0)) next.add(optionId);
        return next;
      }
      if (next.has(optionId)) { next.delete(optionId); return next; }
      const inGroup = group.items.filter(o => next.has(o.id)).length;
      if (mx > 0 && inGroup >= mx) { toast.error(`Choose at most ${mx} from ${group.name}`); return prev; }
      next.add(optionId);
      return next;
    });
  };

  const minsMet = groups.every(g => g.items.filter(o => selected.has(o.id)).length >= (Number(g.min) || 0));
  const canConfirm = (!hasVariants || !!variant) && minsMet;

  const basePrice = hasVariants ? (Number(variant?.price) || 0) : (Number(item.price) || 0);
  const addonTotal = groups.reduce((s, g) =>
    s + g.items.filter(o => selected.has(o.id)).reduce((t, o) => t + (Number(o.price) || 0), 0), 0);
  const unitPrice = Math.round((basePrice + addonTotal) * 100) / 100;

  const confirm = () => {
    if (!canConfirm) return;
    const addons = [];
    const optionIds = [];
    for (const g of groups) for (const o of g.items) {
      if (selected.has(o.id)) { addons.push({ id: o.id, name: o.name, price: Number(o.price) || 0 }); optionIds.push(o.id); }
    }
    onConfirm({
      item_id: item.id,
      name: item.name,
      variant_id: variant ? variant.id : null,
      variant_label: variant ? variant.label : null,
      addon_option_ids: optionIds,
      addons,
      qty,
      unit_price: unitPrice,
    });
  };

  const ruleLabel = (g) => {
    const mn = Number(g.min) || 0, mx = Number(g.max) || 0;
    if (mn >= 1) return mx > mn ? `Choose ${mn}–${mx}` : `Choose ${mn}`;
    return mx > 0 ? `Choose up to ${mx}` : "Optional";
  };

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }}
      onClick={onCancel}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: "min(440px, 96vw)", maxHeight: "88vh", display: "flex", flexDirection: "column", background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 16, overflow: "hidden" }}
      >
        {/* Header */}
        <div style={{ flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <span style={{ fontWeight: 700, color: "#fff", fontSize: 15 }}>{item.name}</span>
          <button type="button" onClick={onCancel} style={{ color: "#A0A0A0", background: "none", border: "none", cursor: "pointer" }}><XCircle size={18} /></button>
        </div>

        {/* Body — scrollable */}
        <div style={{ flex: 1, overflowY: "auto", padding: "14px 18px", minHeight: 0 }}>
          {hasVariants && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", margin: "0 0 8px" }}>
                <p style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "#A0A0A0", margin: 0 }}>Choose one</p>
                <span style={{ fontSize: 10, color: variant ? "#666" : "#f87171" }}>Required</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {variants.map(v => {
                  const sel = variantId === v.id;
                  return (
                    <button key={v.id} type="button" onClick={() => setVariantId(v.id)}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 12px", borderRadius: 8, cursor: "pointer", textAlign: "left",
                               background: sel ? "rgba(157,0,255,0.15)" : "#1A1A1A",
                               border: `1px solid ${sel ? "#9D00FF" : "rgba(255,255,255,0.1)"}`, color: "#fff" }}>
                      <span style={{ fontSize: 13 }}>{v.label}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: sel ? "#fff" : "#9D00FF" }}>₹{Number(v.price) || 0}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {groups.map(g => {
            const cnt = g.items.filter(o => selected.has(o.id)).length;
            const short = (Number(g.min) || 0) > cnt;
            return (
              <div key={g.id} style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", margin: "0 0 8px" }}>
                  <p style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "#A0A0A0", margin: 0 }}>{g.name}</p>
                  <span style={{ fontSize: 10, color: short ? "#f87171" : "#666" }}>{ruleLabel(g)}</span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {g.items.map(o => {
                    const sel = selected.has(o.id);
                    const inactive = o.is_active === false;
                    const price = Number(o.price) || 0;
                    return (
                      <button key={o.id} type="button" disabled={inactive} onClick={() => toggle(g, o.id, !inactive)}
                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8,
                                 cursor: inactive ? "not-allowed" : "pointer", textAlign: "left", opacity: inactive ? 0.4 : 1,
                                 background: sel ? "rgba(157,0,255,0.15)" : "#1A1A1A",
                                 border: `1px solid ${sel ? "#9D00FF" : "rgba(255,255,255,0.1)"}`, color: "#fff" }}>
                        <span style={{ fontSize: 13 }}>{o.name}{inactive ? " · Unavailable" : ""}</span>
                        <span style={{ fontSize: 12, color: "#A0A0A0" }}>{price > 0 ? `+₹${price}` : ""}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer — qty + confirm */}
        <div style={{ flexShrink: 0, padding: "12px 18px 14px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button type="button" onClick={() => setQty(q => Math.max(1, q - 1))}
                style={{ width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", cursor: "pointer" }}><Minus size={13} /></button>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#fff", width: 20, textAlign: "center" }}>{qty}</span>
              <button type="button" onClick={() => setQty(q => q + 1)}
                style={{ width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8, background: "rgba(157,0,255,0.2)", border: "1px solid rgba(157,0,255,0.4)", color: "#9D00FF", cursor: "pointer" }}><Plus size={13} /></button>
            </div>
            <span style={{ fontSize: 16, fontWeight: 700, color: "#fff" }}>₹{(unitPrice * qty).toFixed(0)}</span>
          </div>
          <button type="button" onClick={confirm} disabled={!canConfirm}
            style={{ width: "100%", padding: 11, borderRadius: 10, border: "none",
                     background: canConfirm ? "#9D00FF" : "rgba(157,0,255,0.35)", color: "#fff", fontWeight: 700, fontSize: 13,
                     cursor: canConfirm ? "pointer" : "not-allowed" }}>
            {hasVariants && !variant ? "Choose an option" : "Add to order"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── New Order Modal (Counter Order Taking) ────────────────────────────────────

// Client mirror of the backend room_service_open_now (server.py): an IST HH:MM window that may
// span midnight; start==end or unparseable means 24h open. Used only to decide whether to OFFER the
// Room tab — the server still enforces the same rule on placement.
function roomServiceOpenNow(venue) {
  const toMins = (t) => {
    const h = parseInt((t || "").slice(0, 2), 10), m = parseInt((t || "").slice(3, 5), 10);
    return Number.isNaN(h) || Number.isNaN(m) ? null : h * 60 + m;
  };
  const sm = toMins(venue?.room_service_start || "07:00");
  const em = toMins(venue?.room_service_end || "23:00");
  if (sm == null || em == null || sm === em) return true;
  const d = new Date();
  const nm = (d.getUTCHours() * 60 + d.getUTCMinutes() + 330) % 1440;   // now in IST (UTC+5:30)
  return em > sm ? (sm <= nm && nm <= em) : (nm >= sm || nm <= em);
}

// Which order types this venue can create right now, from the config serialize_user ships. Mirrors
// _validate_order_type_rules on the server — never invents rules. Room only while the window is open.
function orderTypesFor(venue) {
  const mode = venue?.ordering_mode || "takeaway";
  const out = [];
  if (mode === "takeaway" || mode === "both") out.push("takeaway");
  if (mode === "dine_in" || mode === "both") out.push("dine_in");
  if (venue?.room_service_enabled && roomServiceOpenNow(venue)) out.push("room");
  return out.length ? out : ["takeaway"];
}
const ORDER_TYPE_LABEL = { takeaway: "Takeaway", dine_in: "Dine-in", room: "Room" };

// Prompt Y — the customer control for dine-in and round mode. Phone-first typeahead against the
// existing customers (GET /customers?q=, which already returns points_balance), so a waiter can tell
// a guest "you have 330 points" standing at the table. A number with no match becomes a new customer
// (name + phone, nothing else). value/onChange thread the modal's shared customerPhone/customerName.
function CustomerPicker({ value, onChange, onClear }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");

  // Live typeahead (debounced) — /customers?q= matches phone OR name and returns points_balance.
  useEffect(() => {
    const term = q.trim();
    if (!open || term.length < 3) { setResults([]); setLoading(false); return; }
    let active = true; setLoading(true);
    const t = setTimeout(() => {
      api.get("/customers", { params: { q: term } })
        .then(({ data }) => { if (active) setResults((data || []).slice(0, 6)); })
        .catch(() => { if (active) setResults([]); })
        .finally(() => { if (active) setLoading(false); });
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [q, open]);

  const digits = q.replace(/\D/g, "");
  const canAddNew = digits.length === 10 && !results.some((r) => r.phone === digits);
  const reset = () => { setOpen(false); setQ(""); setResults([]); setNewName(""); };
  const pick = (c) => { onChange({ phone: c.phone, name: c.name, balance: c.points_balance }); reset(); };
  const addNew = () => { onChange({ phone: digits, name: newName.trim() || "Guest", balance: null }); reset(); };
  const inputStyle = { width: "100%", background: "#1A1A1A", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8, padding: "9px 10px", color: "#fff", fontSize: 14, outline: "none", boxSizing: "border-box" };

  if (value && !open) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(157,0,255,0.1)", border: "1px solid rgba(157,0,255,0.35)", borderRadius: 8, padding: "6px 10px" }}>
        <User size={14} style={{ color: "#9D00FF", flexShrink: 0 }} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value.name}</div>
          <div style={{ fontSize: 11, color: "#A0A0A0" }}>{value.phone}{value.balance != null ? ` · ${value.balance} pts` : ""}</div>
        </div>
        <button type="button" onClick={() => setOpen(true)} style={{ fontSize: 11, fontWeight: 600, color: "#9D00FF", background: "none", border: "none", cursor: "pointer", minHeight: 44, padding: "0 6px" }}>Change</button>
        <button type="button" onClick={onClear} aria-label="Remove customer" style={{ color: "#A0A0A0", background: "none", border: "none", cursor: "pointer", minHeight: 44, minWidth: 32, display: "flex", alignItems: "center", justifyContent: "center" }}><X size={15} /></button>
      </div>
    );
  }
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)}
        style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", minHeight: 44, padding: "0 12px", borderRadius: 8, background: "rgba(255,255,255,0.05)", border: "1px dashed rgba(255,255,255,0.2)", color: "#A0A0A0", cursor: "pointer", fontSize: 13, fontWeight: 500 }}>
        <User size={14} /> Add customer
      </button>
    );
  }
  return (
    <div>
      <input autoFocus type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Phone or name" style={inputStyle} />
      <div style={{ marginTop: 6 }}>
        {loading && <div style={{ fontSize: 11, color: "#777", padding: "4px 2px" }}>Searching…</div>}
        {results.map((c) => (
          <button key={c.id} type="button" onClick={() => pick(c)}
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, width: "100%", minHeight: 44, padding: "0 10px", marginBottom: 4, borderRadius: 8, background: "#1A1A1A", border: "1px solid rgba(255,255,255,0.08)", color: "#fff", cursor: "pointer", textAlign: "left" }}>
            <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13 }}>{c.name} <span style={{ color: "#777" }}>· {c.phone}</span></span>
            <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 700, color: "#9D00FF" }}>{c.points_balance} pts</span>
          </button>
        ))}
        {canAddNew && (
          <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
            <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Name (new customer)" style={{ ...inputStyle, flex: 1 }} />
            <button type="button" onClick={addNew} style={{ flexShrink: 0, minHeight: 44, padding: "0 14px", borderRadius: 8, background: "#9D00FF", border: "none", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Add</button>
          </div>
        )}
        {!loading && q.trim().length >= 3 && results.length === 0 && !canAddNew && (
          <div style={{ fontSize: 11, color: "#777", padding: "4px 2px" }}>Type a 10-digit number to add a new customer.</div>
        )}
      </div>
      <button type="button" onClick={reset} style={{ marginTop: 6, fontSize: 11, color: "#A0A0A0", background: "none", border: "none", cursor: "pointer", minHeight: 36, padding: "0 2px" }}>Cancel</button>
    </div>
  );
}

// checkId/onRoundAdded put this modal in "round" mode: the same item picker + customisation
// sheet, but it adds the cart to a running bill (POST /checks/{id}/rounds) instead of creating a
// standalone order. Customer is captured here too now (Prompt Y). Exported so Tables reuses it.
export function NewOrderModal({ venue, onClose, onOrderPlaced, checkId, servicePointId, onRoundAdded }) {
  // Order type (Prompt S, Commit 1). Only meaningful when NOT in round mode. The tabs come from the
  // venue config, never a hard-coded three; a venue with one type shows a label, not a selector.
  const availableTypes = orderTypesFor(venue);
  const [orderType, setOrderType] = useState(availableTypes[0]);
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);   // {id, label} service point
  const [openChecks, setOpenChecks] = useState([]);           // open checks, for the picker's occupied state (T)
  const [roomNumber, setRoomNumber] = useState("");
  const [covers, setCovers] = useState(String(venue?.default_covers ?? 2));  // dine-in (Commit 4)
  const [showCustomer, setShowCustomer] = useState(false);  // room: optional customer behind a tap
  const [showNote, setShowNote] = useState(false);          // order note behind a tap (all types)
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  // Cart is a line array (not {item_id: qty}): a variant item has no single price and two
  // different customisations of one item must be two lines. Each line: {key, item_id, name,
  // variant_id, variant_label, addon_option_ids, addons:[{id,name,price}], qty, unit_price}.
  const [lines, setLines] = useState([]);
  const [addonCatalog, setAddonCatalog] = useState({});
  const [customiseItem, setCustomiseItem] = useState(null); // item being customised, or null
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState(null);
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerBalance, setCustomerBalance] = useState(null);  // points of a freshly-picked member, for "you have N points" at the table
  const [vehicleNumber, setVehicleNumber] = useState("");  // takeaway only, when the venue flag is on
  const [lookupDone, setLookupDone] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [specialNote, setSpecialNote] = useState("");
  const [placing, setPlacing] = useState(false);
  const [loadingItems, setLoadingItems] = useState(true);

  // Fetch menu items on mount. In round mode the check's table is the service point in context,
  // so the server hides items marked unavailable there (8b).
  useEffect(() => {
    api.get("/orders/menu-items", servicePointId ? { params: { service_point_id: servicePointId } } : undefined)
      .then(({ data }) => {
        setMenuItems(data.items || []);
        setCategories(data.categories || []);
        setAddonCatalog(data.addon_catalog || {});
        if (data.categories?.length) setActiveCat(data.categories[0].id);
      })
      .catch(() => toast.error("Failed to load menu"))
      .finally(() => setLoadingItems(false));
  }, [servicePointId]);

  // Tables for the dine-in picker — fetched once, the first time Dine-in is selected (Commit 1).
  // Uses the staff-readable /service-points/tables (the admin tree is not reachable from the
  // counter). Not in round mode: the table is already fixed by the check we are adding to.
  useEffect(() => {
    if (checkId || orderType !== "dine_in" || tables.length) return;
    api.get("/service-points/tables")
      .then(({ data }) => setTables(data.tables || []))
      .catch(() => toast.error("Failed to load tables"));
  }, [orderType, checkId, tables.length]);

  // Open checks for the picker's occupied state (Prompt T, Commit 3): which tables have a running
  // bill and its total. Fetched when Dine-in opens and polled every 8s so the marks don't go stale
  // while the modal sits open; placeOrder reuses this same GET rather than fetching a second time.
  useEffect(() => {
    if (checkId || orderType !== "dine_in") return;
    let active = true;
    const load = () => api.get("/checks?status=open")
      .then(({ data }) => { if (active) setOpenChecks(data.checks || []); })
      .catch(() => {});
    load();
    const id = setInterval(load, 8000);
    return () => { active = false; clearInterval(id); };
  }, [orderType, checkId]);

  // Round mode (Prompt Y): a waiter coming back to an already-open table. Pre-fill the customer the
  // check already holds so the control shows the attached name rather than "Add customer". Balance
  // isn't stored on the check — it shows only for a member picked fresh from the search.
  useEffect(() => {
    if (!checkId) return;
    api.get(`/checks/${checkId}`)
      .then(({ data }) => {
        if (data?.customer_phone) { setCustomerPhone(data.customer_phone); setCustomerName(data.customer_name || ""); }
      })
      .catch(() => {});
  }, [checkId]);

  // Phone lookup
  const handlePhoneChange = async (val) => {
    const digits = val.replace(/\D/g, "").slice(0, 10);
    setCustomerPhone(digits);
    setLookupDone(false);
    if (digits.length === 10) {
      setLookupLoading(true);
      try {
        const { data: r } = await api.post(`/public/menu/points-check`, {
          slug: venue?.menu_slug || "",
          phone: digits,
        });
        if (r.found && r.first_name) setCustomerName(r.first_name);
        else if (r.returning_orderer && r.first_name) setCustomerName(r.first_name);
      } catch (_) {}
      setLookupDone(true);
      setLookupLoading(false);
    } else {
      setCustomerName("");
    }
  };

  const subtotal = lines.reduce((s, l) => s + l.unit_price * l.qty, 0);
  const cartCount = lines.reduce((s, l) => s + l.qty, 0);

  // Attach the picked customer to the check after the round is placed (Prompt Y). Non-fatal: the
  // round and the bill are the real action; a failed attach warns but never fails the placement.
  const attachCustomerToCheck = async (cid) => {
    if (!customerPhone || customerPhone.length !== 10) return;
    try {
      await api.post(`/checks/${cid}/customer`, { phone: customerPhone, name: customerName.trim() || undefined });
    } catch (_) {
      toast.error("Order placed, but the customer couldn't be attached — add them from the bill.");
    }
  };

  // The dine-in / round-mode customer control's value and setters, over the shared customer state.
  // In round mode the check already exists, so a pick/clear persists immediately — the waiter came
  // back to attach a number and may not add a round. In new-order mode there is no check yet, so the
  // customer rides in local state and is attached when the first round lands (attachCustomerToCheck).
  const customerValue = customerPhone ? { phone: customerPhone, name: customerName, balance: customerBalance } : null;
  const setCustomer = async ({ phone, name, balance }) => {
    setCustomerPhone(phone); setCustomerName(name || ""); setCustomerBalance(balance ?? null);
    if (checkId) {
      try { await api.post(`/checks/${checkId}/customer`, { phone, name: (name || "").trim() || undefined }); }
      catch (_) { toast.error("Couldn't attach the customer — try again."); }
    }
  };
  const clearCustomer = async () => {
    setCustomerPhone(""); setCustomerName(""); setCustomerBalance(null);
    if (checkId) {
      try { await api.delete(`/checks/${checkId}/customer`); }
      catch (_) { toast.error("Couldn't remove the customer — try again."); }
    }
  };

  // Add a resolved line, merging with an identical one (same cartLineKey) by quantity.
  const addLine = (line) => {
    const key = cartLineKey(line.item_id, line.variant_id, line.addon_option_ids);
    setLines(prev => {
      const i = prev.findIndex(l => l.key === key);
      if (i >= 0) return prev.map((l, j) => j === i ? { ...l, qty: l.qty + line.qty } : l);
      return [...prev, { ...line, key, note: line.note || "" }];
    });
  };
  const adjustLine = (key, delta) =>
    setLines(prev => prev.map(l => l.key === key ? { ...l, qty: l.qty + delta } : l).filter(l => l.qty > 0));
  // Per-line note (Commit 2): the note is a property of the cart LINE, so different selections of one
  // item carry different notes. placeOrder threads it to the KOT (and to a dine-in round, same path).
  const updateLineNote = (key, note) =>
    setLines(prev => prev.map(l => l.key === key ? { ...l, note } : l));

  // Open-check state for the dine-in picker (Prompt T, Commit 3): the table's running bill, if any.
  const tableCheck = (spId) => openChecks.find((c) => (c.service_point_ids || []).includes(spId)) || null;
  const selectedCheck = selectedTable ? tableCheck(selectedTable.id) : null;
  const checkTotal = (c) => Math.round((c?.bill || {}).rounded_total ?? (c?.bill || {}).total ?? 0);
  const placeVerb = orderType === "dine_in"
    ? (selectedCheck ? `Add to C${selectedCheck.check_number}` : "Start bill")
    : "Place Order";
  // A plain item adds directly; a customisable one opens the sheet (handled in the grid).
  const plainKey = (id) => cartLineKey(id, null, []);
  const plainQty = (id) => { const l = lines.find(x => x.key === plainKey(id)); return l ? l.qty : 0; };
  const addPlain = (item) => addLine({
    item_id: item.id, name: item.name, variant_id: null, variant_label: null,
    addon_option_ids: [], addons: [], qty: 1, unit_price: Number(item.price) || 0,
  });

  // Search: an exact item_code match (e.g. "403") jumps straight to that item, ignoring the
  // category filter; otherwise fall back to a name (or partial-code) substring match.
  const filteredItems = (() => {
    const q = (search || "").trim();
    if (!q) return menuItems.filter(it => !activeCat || it.category_id === activeCat);
    const codeQ = q.toUpperCase().replace(/[^A-Z0-9]/g, "");
    const exact = codeQ ? menuItems.filter(it => (it.item_code || "") === codeQ) : [];
    if (exact.length) return exact;
    const nameQ = q.toLowerCase();
    return menuItems.filter(it =>
      (!activeCat || it.category_id === activeCat) &&
      (it.name.toLowerCase().includes(nameQ) || (it.item_code || "").toLowerCase().includes(nameQ)));
  })();

  const placeOrder = async () => {
    if (cartCount === 0) { toast.error("Add at least one item"); return; }
    const items = lines.map(l => ({
      item_id: l.item_id,
      variant_id: l.variant_id || undefined,
      addon_option_ids: l.addon_option_ids.length ? l.addon_option_ids : undefined,
      qty: l.qty,
      note: (l.note || "").trim(),
    }));
    setPlacing(true);
    try {
      if (checkId) {
        // Round mode — add the cart to the running bill. Any customer the waiter picked here was
        // already attached immediately (round mode persists on pick), so nothing to do for it now.
        await api.post(`/checks/${checkId}/rounds`, { items, origin: "staff" });
        toast.success("Added to the bill");
        if (onRoundAdded) onRoundAdded();
        onClose();
        return;
      }

      // Dine-in is NOT a dine_in order — it resolves to a running check so the kitchen and the
      // Tables floor see it (Commit 1; Commit 0 patches the guest path the same way). Find the
      // table's open check, else open one — handling the one-check-per-table 409 by re-reading and
      // adding to the winner's check — then add the cart as a round.
      if (orderType === "dine_in") {
        if (!selectedTable) { toast.error("Pick a table"); setPlacing(false); return; }
        const spId = selectedTable.id;
        // Reuse the picker's open-checks state (same GET) rather than fetching again; only re-read
        // fresh if we must open a new check and lose the one-per-table race (409).
        let check = tableCheck(spId);
        if (!check) {
          try {
            const { data: opened } = await api.post("/checks", {
              service_point_id: spId,
              covers: Number(covers) || undefined,
            });
            check = opened;
          } catch (err) {
            if (err?.response?.status === 409) {   // lost the open race — reuse the winner's check
              const { data } = await api.get("/checks?status=open");
              check = (data.checks || []).find((c) => (c.service_point_ids || []).includes(spId)) || null;
            }
            if (!check) throw err;
          }
        }
        await api.post(`/checks/${check.id}/rounds`, { items, origin: "staff" });
        await attachCustomerToCheck(check.id);
        toast.success(`Added to ${selectedTable.label} · bill C${check.check_number}`);
        onOrderPlaced();
        onClose();
        return;
      }

      if (orderType === "room") {
        if (!roomNumber.trim()) { toast.error("Room number is required"); setPlacing(false); return; }
        // customer_name is required by the API, but customer details are optional for room — default
        // the name to the room so the staff need not type one (no phone → no loyalty, as expected).
        const { data } = await api.post("/orders", {
          slug: venue?.menu_slug || "",
          customer_name: customerName.trim() || `Room ${roomNumber.trim()}`,
          customer_phone: (customerPhone && customerPhone.length === 10) ? customerPhone : undefined,
          order_type: "room",
          room_number: roomNumber.trim(),
          items,
          special_note: specialNote.trim() || undefined,
          enroll_loyalty: true,
        });
        toast.success(`Room order #${data.order_number} placed — ₹${data.total}`);
        onOrderPlaced();
        onClose();
        return;
      }

      // Takeaway (default): customer name + phone are required — the loyalty enrolment hook.
      if (!customerName.trim()) { toast.error("Customer name is required"); setPlacing(false); return; }
      if (!customerPhone || customerPhone.length !== 10) { toast.error("Valid 10-digit mobile number required"); setPlacing(false); return; }
      const { data } = await api.post("/orders", {
        slug: venue?.menu_slug || "",
        customer_name: customerName.trim(),
        customer_phone: customerPhone,
        order_type: "takeaway",
        vehicle_number: (venue?.takeaway_vehicle_enabled && vehicleNumber.trim()) ? vehicleNumber.trim() : undefined,
        items,
        special_note: specialNote.trim() || undefined,
        enroll_loyalty: true,
      });
      toast.success(`Order #${data.order_number} placed — ₹${data.total}`);
      onOrderPlaced();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to place order");
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, background: "rgba(0,0,0,0.85)", backdropFilter: "blur(4px)" }}
    >
      <div
        className="w-[96vw] md:w-[min(900px,96vw)] h-[96vh] md:h-[min(88vh,680px)]"
        style={{
          display: "flex",
          flexDirection: "column",
          background: "#0A0A0A",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: 16,
          overflow: "hidden",
        }}
      >

        {/* Header */}
        <div style={{ flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ShoppingCart size={16} style={{ color: "#9D00FF" }} />
            <span style={{ fontWeight: 700, color: "#fff", fontSize: 15 }}>{checkId ? "Add to Bill" : "New Counter Order"}</span>
          </div>
          <button type="button" onClick={onClose} style={{ color: "#A0A0A0", background: "none", border: "none", cursor: "pointer" }}>
            <XCircle size={18} />
          </button>
        </div>

        {/* Body — stacked on mobile, side-by-side grid on tablet/desktop */}
        <div
          className="flex-1 flex flex-col md:grid md:grid-cols-[1fr_280px] min-h-0 overflow-hidden"
        >

          {/* LEFT — item picker: 50vh max on mobile, fills available on desktop */}
          <div
            className="max-h-[50vh] md:max-h-none"
            style={{
              display: "flex", flexDirection: "column",
              borderRight: "1px solid rgba(255,255,255,0.06)",
              minHeight: 0, overflow: "hidden",
            }}
          >

            {/* Search */}
            <div style={{ flexShrink: 0, padding: "10px 12px 6px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#1A1A1A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "7px 12px" }}>
                <Search size={13} style={{ color: "#555", flexShrink: 0 }} />
                <input
                  type="text"
                  value={search}
                  onChange={e => { setSearch(e.target.value); setActiveCat(null); }}
                  placeholder="Search items…"
                  style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: "#fff", fontSize: 14 }}
                />
              </div>
            </div>

            {/* Category pills — horizontal strip on MOBILE only (Commit 3): a vertical rail below
                the breakpoint would eat the width the items grid needs. */}
            {!search && (
              <div className="md:hidden flex" style={{ flexShrink: 0, gap: 6, padding: "4px 12px 8px", overflowX: "auto" }}>
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setActiveCat(cat.id)}
                    style={{
                      flexShrink: 0, fontSize: 11, fontWeight: 500,
                      display: "inline-flex", alignItems: "center", minHeight: 44,
                      padding: "0 14px", borderRadius: 20, border: "none",
                      cursor: "pointer", transition: "all .15s",
                      background: activeCat === cat.id ? "#9D00FF" : "rgba(255,255,255,0.07)",
                      color: activeCat === cat.id ? "#fff" : "#A0A0A0",
                    }}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            )}

            {/* Rail + grid. On md+ a VERTICAL category rail sits beside the grid (Commit 3) — all 20-odd
                categories without a sideways swipe during a rush; on mobile the rail is hidden (the strip
                above serves) so the grid keeps its width. Search behaviour is untouched. */}
            <div style={{ flex: 1, display: "flex", minHeight: 0, overflow: "hidden" }}>
              {!search && (
                <div className="hidden md:flex" style={{ flexShrink: 0, flexDirection: "column", gap: 2, width: 132, overflowY: "auto", padding: "4px 6px 12px", borderRight: "1px solid rgba(255,255,255,0.06)" }}>
                  {categories.map(cat => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setActiveCat(cat.id)}
                      style={{
                        flexShrink: 0, textAlign: "left", fontSize: 11, fontWeight: 500,
                        display: "flex", alignItems: "center", minHeight: 44,
                        padding: "0 10px", borderRadius: 8, border: "none",
                        borderLeft: `2px solid ${activeCat === cat.id ? "#9D00FF" : "transparent"}`,
                        cursor: "pointer", transition: "all .15s",
                        background: activeCat === cat.id ? "rgba(157,0,255,0.18)" : "transparent",
                        color: activeCat === cat.id ? "#fff" : "#A0A0A0",
                      }}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              )}

            {/* Items grid — scrollable */}
            <div style={{ flex: 1, overflowY: "auto", padding: "4px 12px 12px" }}>
              {loadingItems ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 120, color: "#A0A0A0", fontSize: 13 }}>Loading…</div>
              ) : filteredItems.length === 0 ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 120, color: "#555", fontSize: 13 }}>No items found</div>
              ) : (
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                  {/* 2 columns on a portrait tablet so a wrapped item name has room to breathe; the
                      third column only appears at lg (1024px) and up, where there's width to spare. */}
                  {filteredItems.map(item => {
                    // Customisable items (variants / add-ons) open the sheet; plain items add
                    // directly. The sheet returns a line keyed by its full selection, so a
                    // variant item (price: null) and repeat customisations both behave.
                    if (item.customisable) {
                      const vs = Array.isArray(item.variants) ? item.variants : [];
                      const from = vs.length
                        ? Math.min(...vs.map(v => Number(v.price) || 0))
                        : (Number(item.price) || 0);
                      return (
                        // The WHOLE tile is the tap target and it opens the customisation sheet — the
                        // POS these staff know adds by tapping the tile. The old 99px "Customise" button
                        // crushed the name to ~16px (65 of 278 items); now the name gets the full card
                        // width. "Options" is a compact marker on the price line, not a button.
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => setCustomiseItem(item)}
                          aria-label={`Customise ${item.name}`}
                          style={{
                            display: "block", width: "100%", textAlign: "left", font: "inherit",
                            cursor: "pointer", boxSizing: "border-box",
                            background: "#1A1A1A", border: "1px solid rgba(255,255,255,0.08)",
                            borderRadius: 10, padding: "10px 12px",
                          }}
                        >
                          {/* Two-line clamp, full card width — a waiter must read "Chilli Garlic Fried
                              Rice", not "C…". */}
                          <p style={{ fontSize: 13, fontWeight: 500, color: "#fff", margin: 0, display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 2, overflow: "hidden", wordBreak: "break-word", lineHeight: 1.3 }}>{item.name}</p>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: 3 }}>
                            <span style={{ fontSize: 11, color: "#9D00FF", fontWeight: 600 }}>from ₹{from}</span>
                            <span style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10, fontWeight: 700, color: "#9D00FF", background: "rgba(157,0,255,0.14)", border: "1px solid rgba(157,0,255,0.35)", borderRadius: 6, padding: "2px 7px" }}>
                              <SlidersHorizontal size={11} /> Options
                            </span>
                          </div>
                        </button>
                      );
                    }
                    const qty = plainQty(item.id);
                    return (
                      // The WHOLE tile is the tap target and each tap adds one (the POS pattern: repeated
                      // taps increment). No corner button stealing width — the name gets the full card.
                      // Reducing / removing is in the cart (W-1). Quantity shows as a compact badge on
                      // the price line; the purple border also marks "in cart". Nothing resizes on tap.
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => addPlain(item)}
                        aria-label={qty === 0 ? `Add ${item.name}` : `Add one more ${item.name}, ${qty} in cart`}
                        style={{
                          display: "block", width: "100%", textAlign: "left", font: "inherit",
                          cursor: "pointer", boxSizing: "border-box",
                          background: "#1A1A1A",
                          border: `1px solid ${qty > 0 ? "rgba(157,0,255,0.4)" : "rgba(255,255,255,0.08)"}`,
                          borderRadius: 10, padding: "10px 12px",
                        }}
                      >
                        <p style={{ fontSize: 13, fontWeight: 500, color: "#fff", margin: 0, display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 2, overflow: "hidden", wordBreak: "break-word", lineHeight: 1.3 }}>{item.name}</p>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: 3 }}>
                          <span style={{ fontSize: 11, color: "#9D00FF", fontWeight: 600 }}>₹{Number(item.price) || 0}</span>
                          {qty > 0 && (
                            <span style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 20, height: 20, padding: "0 6px", borderRadius: 999, background: "rgba(157,0,255,0.9)", color: "#fff", fontSize: 12, fontWeight: 800, lineHeight: 1 }}>{qty}</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            </div>
          </div>

          {/* RIGHT — customer + cart + place order */}
          <div
            className="md:w-[280px]"
            style={{
              display: "flex", flexDirection: "column",
              minHeight: 0, overflow: "hidden",
              borderTop: "1px solid rgba(255,255,255,0.06)",
            }}
          >

            {/* Round mode (Prompt Y): the waiter is back at an already-open table and can attach the
                customer they forgot — or see the one already on the bill (pre-filled above). */}
            {checkId && (
              <div style={{ flexShrink: 0, padding: "12px 14px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: "#A0A0A0", display: "block", marginBottom: 8 }}>Customer</span>
                <CustomerPicker value={customerValue} onChange={setCustomer} onClear={clearCustomer} />
              </div>
            )}

            {/* Order type (Commit 1) — tabs from venue config; a single type renders as a label.
                Not shown in round mode (checkId): that modal only adds rounds to one existing check. */}
            {!checkId && (
              <div style={{ flexShrink: 0, padding: "12px 14px 10px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                {availableTypes.length > 1 ? (
                  <div style={{ display: "flex", gap: 6, background: "rgba(255,255,255,0.05)", borderRadius: 8, padding: 3 }}>
                    {availableTypes.map((t) => (
                      <button key={t} type="button" onClick={() => setOrderType(t)}
                        style={{
                          flex: 1, fontSize: 12, fontWeight: 600, padding: "7px 4px", borderRadius: 6, border: "none",
                          cursor: "pointer", transition: "all .15s", minHeight: 44,
                          background: orderType === t ? "#9D00FF" : "transparent",
                          color: orderType === t ? "#fff" : "#A0A0A0",
                        }}>
                        {ORDER_TYPE_LABEL[t]}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p style={{ fontSize: 13, fontWeight: 600, color: "#fff", margin: 0 }}>{ORDER_TYPE_LABEL[availableTypes[0]]}</p>
                )}

                {/* Dine-in: pick a table — resolves to a running check on placement, never a dine_in order. */}
                {orderType === "dine_in" && (
                  <div style={{ marginTop: 10 }}>
                    {tables.length === 0 ? (
                      <p style={{ fontSize: 11, color: "#777", margin: 0 }}>No tables configured — set them up in Settings.</p>
                    ) : (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 112, overflowY: "auto" }}>
                        {tables.map((t) => {
                          const chk = tableCheck(t.id);
                          const sel = selectedTable?.id === t.id;
                          return (
                            <button key={t.id} type="button" onClick={() => setSelectedTable({ id: t.id, label: t.label })}
                              style={{
                                fontSize: 11, fontWeight: 600, padding: "6px 10px", borderRadius: 8, cursor: "pointer", minHeight: 34,
                                background: sel ? "rgba(157,0,255,0.25)" : chk ? "rgba(245,158,11,0.12)" : "rgba(255,255,255,0.05)",
                                border: `1px solid ${sel ? "rgba(157,0,255,0.6)" : chk ? "rgba(245,158,11,0.4)" : "rgba(255,255,255,0.1)"}`,
                                color: sel ? "#fff" : chk ? "#FCD34D" : "#A0A0A0",
                              }}>
                              {t.label}{chk ? ` · ₹${checkTotal(chk)}` : ""}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {selectedTable && (
                      <p style={{ fontSize: 11, margin: "6px 0 0", color: selectedCheck ? "#FCD34D" : "#777" }}>
                        {selectedCheck
                          ? `Adds to open bill C${selectedCheck.check_number} · ₹${checkTotal(selectedCheck)}`
                          : `Starts a new bill on ${selectedTable.label}`}
                      </p>
                    )}
                    <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 11, color: "#A0A0A0" }}>Covers</span>
                      <input type="number" min={1} max={40} value={covers} onChange={(e) => setCovers(e.target.value)}
                        style={{ width: 64, background: "#1A1A1A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "7px 10px", color: "#fff", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
                    </div>
                    {/* Customer — a peer of table and covers (Prompt Y). Captured now, at the table,
                        not extracted at the till. Attached to the check when the first round lands. */}
                    <div style={{ marginTop: 10 }}>
                      <span style={{ fontSize: 11, color: "#A0A0A0", display: "block", marginBottom: 6 }}>Customer</span>
                      <CustomerPicker value={customerValue} onChange={setCustomer} onClear={clearCustomer} />
                    </div>
                  </div>
                )}

                {/* Room: room number required. */}
                {orderType === "room" && (
                  <input type="text" value={roomNumber} onChange={(e) => setRoomNumber(e.target.value)}
                    placeholder="Room number *" maxLength={20}
                    style={{ width: "100%", marginTop: 10, background: "#1A1A1A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "9px 10px", color: "#fff", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
                )}
              </div>
            )}

            {/* Takeaway customer — prominent and required; the loyalty hook (Commit 4). Dine-in has no
                customer control (the round carries none; the check collects a phone at settle); room's
                is compact below. */}
            {!checkId && orderType === "takeaway" && (
            <div style={{ flexShrink: 0, padding: "12px 14px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
              <p style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: "#A0A0A0", margin: "0 0 8px" }}>Customer</p>
              <div style={{ position: "relative", marginBottom: 8 }}>
                <Phone size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#555" }} />
                <input
                  type="tel"
                  inputMode="numeric"
                  value={customerPhone}
                  onChange={e => handlePhoneChange(e.target.value)}
                  placeholder="Mobile number *"
                  maxLength={10}
                  style={{ width: "100%", background: "#1A1A1A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "9px 10px 9px 30px", color: "#fff", fontSize: 14, outline: "none", boxSizing: "border-box" }}
                />
                {lookupLoading && (
                  <div style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)" }}>
                    <div className="animate-spin rounded-full border-2 border-white/10 border-t-[#9D00FF]" style={{ width: 13, height: 13 }} />
                  </div>
                )}
              </div>
              {customerPhone.length === 10 && (
                <div style={{ position: "relative" }}>
                  <User size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#555" }} />
                  <input
                    type="text"
                    value={customerName}
                    onChange={e => setCustomerName(e.target.value)}
                    placeholder="Customer name *"
                    autoFocus={lookupDone && !customerName}
                    style={{ width: "100%", background: "#1A1A1A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "9px 10px 9px 30px", color: "#fff", fontSize: 14, outline: "none", boxSizing: "border-box" }}
                  />
                </div>
              )}
              {venue?.takeaway_vehicle_enabled && (
                <div style={{ marginTop: 8 }}>
                  <input
                    type="text"
                    value={vehicleNumber}
                    onChange={e => setVehicleNumber(e.target.value.toUpperCase().slice(0, 15))}
                    placeholder="Vehicle number (optional)"
                    maxLength={15}
                    style={{ width: "100%", background: "#1A1A1A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "9px 10px", color: "#fff", fontSize: 14, outline: "none", boxSizing: "border-box", textTransform: "uppercase" }}
                  />
                </div>
              )}
            </div>
            )}

            {/* Room: customer optional — the folio/room is the identity — behind a compact control. */}
            {!checkId && orderType === "room" && (
              <div style={{ flexShrink: 0, padding: "12px 14px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                {(showCustomer || customerName || customerPhone) ? (
                  <>
                    <input type="tel" inputMode="numeric" value={customerPhone} onChange={(e) => handlePhoneChange(e.target.value)}
                      placeholder="Customer mobile (optional)" maxLength={10}
                      style={{ width: "100%", background: "#1A1A1A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "9px 10px", color: "#fff", fontSize: 14, outline: "none", boxSizing: "border-box", marginBottom: 8 }} />
                    <input type="text" value={customerName} onChange={(e) => setCustomerName(e.target.value)}
                      placeholder="Customer name (optional)"
                      style={{ width: "100%", background: "#1A1A1A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "9px 10px", color: "#fff", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
                  </>
                ) : (
                  <button type="button" onClick={() => setShowCustomer(true)} style={{ fontSize: 12, color: "#9D00FF", background: "none", border: "none", cursor: "pointer", padding: 0 }}>+ Add customer (optional)</button>
                )}
              </div>
            )}

            {/* Cart items — scrollable, capped on mobile */}
            <div
              className="max-h-[25vh] md:max-h-none md:flex-1"
              style={{ overflowY: "auto", padding: "8px 14px", minHeight: 0 }}
            >
              {lines.length === 0 ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", color: "#555", fontSize: 12, textAlign: "center", gap: 6 }}>
                  <ShoppingCart size={22} style={{ opacity: 0.3 }} />
                  <span>Add items from the menu</span>
                </div>
              ) : (
                lines.map(l => {
                  const details = orderLineDetails(l);
                  return (
                    <div key={l.key} style={{ padding: "5px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <p style={{ fontSize: 12, fontWeight: 500, color: "#fff", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.name}</p>
                          {details && <p style={{ fontSize: 10, color: "#B0B0B0", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{details}</p>}
                          <p style={{ fontSize: 11, color: "#555", margin: 0 }}>₹{l.unit_price} × {l.qty}</p>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 3, marginLeft: 6, flexShrink: 0 }}>
                          {/* 44x44 so a finger, not a fingernail, adjusts the bill — this panel is where
                              quantities are reviewed now that the menu card no longer steps (W-2). Minus
                              at qty 1 removes the line (adjustLine filters qty<=0), so it carries a trash
                              glyph there: reduce and remove are the same 44px control, matching the
                              two-button width budget for a 280px panel. */}
                          <button type="button" onClick={() => adjustLine(l.key, -1)}
                            aria-label={l.qty === 1 ? `Remove ${l.name}` : `Remove one ${l.name}`}
                            style={{ width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", cursor: "pointer" }}>
                            {l.qty === 1 ? <Trash2 size={14} /> : <Minus size={14} />}
                          </button>
                          <span style={{ fontSize: 13, fontWeight: 700, color: "#fff", width: 14, textAlign: "center" }}>{l.qty}</span>
                          <button type="button" onClick={() => adjustLine(l.key, 1)} aria-label={`Add one ${l.name}`}
                            style={{ width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8, background: "rgba(157,0,255,0.15)", border: "1px solid rgba(157,0,255,0.3)", color: "#9D00FF", cursor: "pointer" }}>
                            <Plus size={14} />
                          </button>
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 600, color: "#fff", marginLeft: 6, width: 36, textAlign: "right", flexShrink: 0 }}>
                          ₹{(l.unit_price * l.qty).toFixed(0)}
                        </span>
                      </div>
                      {/* Per-item note at entry (Commit 2) — inline, not behind a tap, so the cashier
                          catches it before the KOT prints. */}
                      <input type="text" value={l.note || ""} onChange={(e) => updateLineNote(l.key, e.target.value)}
                        placeholder="Note (e.g. no onion)" maxLength={140}
                        style={{ width: "100%", marginTop: 4, background: "#141414", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6, padding: "4px 8px", color: "#E8B4FF", fontSize: 11, outline: "none", boxSizing: "border-box" }} />
                    </div>
                  );
                })
              )}
            </div>

            {/* Order note — secondary on every type, behind a compact control (Commit 4).
                "Birthday — bring the cake last" is an order property, not a line's. */}
            <div style={{ flexShrink: 0, padding: "8px 14px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
              {(showNote || specialNote) ? (
                <input
                  type="text"
                  value={specialNote}
                  onChange={e => setSpecialNote(e.target.value)}
                  placeholder="Order note (e.g. bring the cake last)"
                  autoFocus={showNote && !specialNote}
                  style={{ width: "100%", background: "#1A1A1A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "7px 10px", color: "#fff", fontSize: 12, outline: "none", boxSizing: "border-box" }}
                />
              ) : (
                <button type="button" onClick={() => setShowNote(true)} style={{ fontSize: 12, color: "#9D00FF", background: "none", border: "none", cursor: "pointer", padding: 0 }}>+ Add order note</button>
              )}
            </div>

            {/* Total + Place Order — always pinned to bottom, never pushed off screen */}
            <div style={{ flexShrink: 0, padding: "10px 14px 14px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <span style={{ fontSize: 13, color: "#A0A0A0" }}>Total</span>
                <span style={{ fontSize: 17, fontWeight: 700, color: "#fff" }}>₹{subtotal.toFixed(0)}</span>
              </div>
              <button
                type="button"
                onClick={placeOrder}
                disabled={placing || cartCount === 0}
                style={{
                  width: "100%", padding: 11, borderRadius: 10, border: "none",
                  background: placing || cartCount === 0 ? "rgba(157,0,255,0.35)" : "#9D00FF",
                  color: "#fff", fontWeight: 700, fontSize: 13,
                  cursor: placing || cartCount === 0 ? "not-allowed" : "pointer",
                  transition: "background .15s",
                }}
              >
                {checkId
                  ? (placing ? "Adding…" : `Add to Bill · ₹${subtotal.toFixed(0)}`)
                  : (placing ? "Placing…" : `${placeVerb} · ₹${subtotal.toFixed(0)}`)}
              </button>
            </div>
          </div>
        </div>
      </div>

      {customiseItem && (
        <CustomiseSheet
          item={customiseItem}
          addonCatalog={addonCatalog}
          onCancel={() => setCustomiseItem(null)}
          onConfirm={(line) => { addLine(line); setCustomiseItem(null); }}
        />
      )}
    </div>
  );
}

// ── Edit Order Modal (Pending orders only — note + add + remove) ──────────────

function EditOrderModal({ order, onClose, onUpdated }) {
  const [items, setItems] = useState(
    order.items.map(it => ({ ...it, _note: it.note || "" }))
  );
  const [menuItems, setMenuItems] = useState([]);
  const [addonCatalog, setAddonCatalog] = useState({});
  const [customiseItem, setCustomiseItem] = useState(null); // item being customised, or null
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [specialNote, setSpecialNote] = useState(order.special_note || "");

  useEffect(() => {
    api.get("/orders/menu-items")
      .then(({ data }) => { setMenuItems(data.items || []); setAddonCatalog(data.addon_catalog || {}); })
      .catch(() => {});
  }, []);

  // Handlers key by list INDEX, not item_id: an order can hold several lines of the same
  // item (different variants / add-ons), and keying on item_id would move them together.
  const updateQty = (idx, delta) => {
    setItems(prev => prev
      .map((it, i) => i === idx ? { ...it, qty: Math.max(0, it.qty + delta) } : it)
      .filter(it => it.qty > 0));
  };

  const updateNote = (idx, note) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, _note: note } : it));
  };

  const addMenuItem = (menuItem) => {
    // Called for PLAIN items only (customisable ones go through the sheet). Merge with an
    // existing plain line of the same item — never a variant / add-on line — so we don't fold
    // a customised line into a plain one.
    setItems(prev => {
      const i = prev.findIndex(it => it.item_id === menuItem.id && !it.variant_id && !(it.addons && it.addons.length));
      if (i >= 0) return prev.map((it, j) => j === i ? { ...it, qty: it.qty + 1 } : it);
      return [...prev, {
        item_id: menuItem.id,
        name: menuItem.name,
        qty: 1,
        unit_price: menuItem.price,
        line_total: menuItem.price,
        variant_id: null,
        addons: [],
        _note: "",
      }];
    });
  };

  // A customised line from the sheet. Merge with an identical existing line (same cartLineKey)
  // so two different customisations stay separate but the same one just bumps quantity.
  const addCustomised = (line) => {
    const key = cartLineKey(line.item_id, line.variant_id, line.addon_option_ids);
    setItems(prev => {
      const i = prev.findIndex(it => cartLineKey(it.item_id, it.variant_id, (it.addons || []).map(a => a.id)) === key);
      if (i >= 0) return prev.map((it, j) => j === i ? { ...it, qty: it.qty + line.qty } : it);
      return [...prev, {
        item_id: line.item_id,
        name: line.name,
        qty: line.qty,
        unit_price: line.unit_price,
        line_total: line.unit_price * line.qty,
        variant_id: line.variant_id,
        variant_label: line.variant_label,
        addons: line.addons,
        _note: "",
      }];
    });
    setCustomiseItem(null);
  };

  const subtotal = items.reduce((s, it) => s + (it.unit_price || 0) * it.qty, 0);

  const filteredMenu = menuItems.filter(m =>
    !search || m.name.toLowerCase().includes(search.toLowerCase())
  // Hide a PLAIN item already in the order (adjust it in the list on the left instead). A
  // customisable item stays — a second, different customisation is a separate line.
  ).filter(m => m.customisable || !items.find(it => it.item_id === m.id));

  const save = async () => {
    if (items.length === 0) { toast.error("Order must have at least one item"); return; }
    setSaving(true);
    try {
      // Round-trip variant_id and add-on ids for every line — PATCH is a full replacement,
      // so omitting them would silently reprice add-on lines lower and hard-fail variant
      // lines with 400 "Please choose an option". addon_option_ids is derived from the
      // stored line's resolved addons ([{id,name,price}]).
      await api.patch(`/orders/${order.id}/items`, {
        items: items.map(it => ({
          item_id: it.item_id,
          variant_id: it.variant_id || undefined,
          addon_option_ids: (it.addons && it.addons.length) ? it.addons.map(a => a.id) : undefined,
          qty: it.qty,
          note: it._note || "",
        })),
        special_note: specialNote.trim() || null,
      });
      toast.success(`Order #${order.order_number} updated`);
      onUpdated();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update order");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(4px)" }}>
      <div className="flex flex-col bg-[#0A0A0A] border border-white/10 rounded-2xl overflow-hidden"
           style={{ width: "min(700px, 96vw)", maxHeight: "88vh", margin: "auto" }}>

        {/* Header */}
        <div className="flex-none flex items-center justify-between px-5 py-3.5 border-b border-white/5">
          <div className="flex items-center gap-2">
            <Pencil size={15} className="text-blue-400" />
            <span className="font-bold text-white">Edit Order #{order.order_number}</span>
            <span className="text-xs text-[#555]">— pending only</span>
          </div>
          <button type="button" onClick={onClose} className="text-[#A0A0A0] hover:text-white transition">
            <XCircle size={18} />
          </button>
        </div>

        <div className="flex-1 flex min-h-0">

          {/* Left — current items */}
          <div className="flex-1 flex flex-col min-h-0 border-r border-white/5">
            <p className="flex-none px-4 pt-3 pb-1 text-xs uppercase tracking-wider text-[#A0A0A0]">
              Current items
            </p>
            <div className="flex-1 overflow-y-auto px-4 pb-3 min-h-0">
              {items.map((it, idx) => (
                <div key={idx} className="py-2 border-b border-white/5 last:border-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => updateQty(idx, -1)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/5 border border-white/10 text-white hover:bg-white/10">
                        <Minus size={11} />
                      </button>
                      <span className="text-sm font-bold text-white w-6 text-center">{it.qty}</span>
                      <button type="button" onClick={() => updateQty(idx, 1)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#9D00FF]/15 border border-[#9D00FF]/30 text-[#9D00FF] hover:bg-[#9D00FF]/25">
                        <Plus size={11} />
                      </button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="block text-sm text-white truncate">{it.name}</span>
                      {orderLineDetails(it) && (
                        <span className="block text-[11px] text-[#B0B0B0] truncate">{orderLineDetails(it)}</span>
                      )}
                    </div>
                    <span className="text-xs text-[#555] shrink-0">
                      ₹{((it.unit_price || 0) * it.qty).toFixed(0)}
                    </span>
                    <button type="button" onClick={() => updateQty(idx, -it.qty)}
                      className="text-red-400 hover:text-red-300 transition">
                      <XCircle size={14} />
                    </button>
                  </div>
                  {/* Per-item note */}
                  <input
                    type="text"
                    value={it._note}
                    onChange={e => updateNote(idx, e.target.value)}
                    placeholder="Note for kitchen (e.g. extra spicy)"
                    className="w-full bg-[#1A1A1A] border border-white/8 rounded-lg px-3 py-1.5 text-xs text-white focus:border-[#9D00FF] focus:outline-none placeholder-[#444]"
                    style={{ fontSize: 13 }}
                  />
                </div>
              ))}
            </div>

            {/* Special order note */}
            <div className="flex-none px-4 py-2 border-t border-white/5">
              <input
                type="text"
                value={specialNote}
                onChange={e => setSpecialNote(e.target.value)}
                placeholder="Order-level note (optional)"
                className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:border-[#9D00FF] focus:outline-none"
                style={{ fontSize: 13 }}
              />
            </div>

            {/* Total + save */}
            <div className="flex-none px-4 pb-4 pt-2 border-t border-white/5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-[#A0A0A0]">Updated total</span>
                <span className="text-lg font-bold text-white">₹{subtotal.toFixed(0)}</span>
              </div>
              <button
                type="button"
                onClick={save}
                disabled={saving || items.length === 0}
                className="w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white font-bold text-sm transition"
              >
                {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>

          {/* Right — add items */}
          <div className="flex flex-col w-60 shrink-0 min-h-0">
            <p className="flex-none px-3 pt-3 pb-1 text-xs uppercase tracking-wider text-[#A0A0A0]">
              Add items
            </p>
            <div className="flex-none px-3 pb-2">
              <div className="flex items-center gap-2 bg-[#1A1A1A] border border-white/10 rounded-lg px-2.5 py-2">
                <Search size={12} className="text-[#555] shrink-0" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search…"
                  className="flex-1 bg-transparent text-white text-xs outline-none placeholder-[#555]"
                  style={{ fontSize: 13 }}
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-3 pb-3 min-h-0">
              {filteredMenu.map(m => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => m.customisable ? setCustomiseItem(m) : addMenuItem(m)}
                  className="w-full flex items-center justify-between py-2 border-b border-white/5 last:border-0 hover:bg-white/3 rounded transition text-left"
                >
                  <span className="text-xs text-white truncate">{m.name}</span>
                  <div className="flex items-center gap-1.5 ml-2 shrink-0">
                    <span className="text-xs text-[#9D00FF]">{m.customisable ? "Customise" : `₹${m.price}`}</span>
                    <div className="w-5 h-5 flex items-center justify-center rounded bg-[#9D00FF]/15 border border-[#9D00FF]/30 text-[#9D00FF]">
                      <Plus size={10} />
                    </div>
                  </div>
                </button>
              ))}
              {filteredMenu.length === 0 && (
                <p className="text-xs text-[#555] text-center py-4">No more items to add</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {customiseItem && (
        <CustomiseSheet
          item={customiseItem}
          addonCatalog={addonCatalog}
          onCancel={() => setCustomiseItem(null)}
          onConfirm={addCustomised}
        />
      )}
    </div>
  );
}

// ── Order Card ────────────────────────────────────────────────────────────────

function OrderCard({ order, onStatusChange, onPayment, onCancel, onEdit, venueName, printWidthMm }) {
  const [acting, setActing] = useState(false);
  const [time, setTime] = useState(elapsed(order.created_at));

  useEffect(() => {
    const t = setInterval(() => setTime(elapsed(order.created_at)), 10000);
    return () => clearInterval(t);
  }, [order.created_at]);

  const diffMin = Math.floor((Date.now() - new Date(order.created_at).getTime()) / 60000);
  const urgencyBorder = diffMin >= 20
    ? "border-red-500/60"
    : diffMin >= 10
    ? "border-amber-500/60"
    : "border-white/10";
  const timerColor = diffMin >= 20
    ? "text-red-400"
    : diffMin >= 10
    ? "text-amber-400"
    : "text-[#555]";

  const move = async (status) => {
    setActing(true);
    try {
      await api.patch(`/orders/${order.id}/status`, { status });
      onStatusChange(order.id, status);
      if (status === "ready") {
        toast.success(order.order_type === "room"
          ? `#${order.order_number} ready`
          : `#${order.order_number} ready — WhatsApp sent`);
      }
      if (status === "delivered") toast.success(`#${order.order_number} delivered to Room ${order.room_number}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    } finally {
      setActing(false);
    }
  };

  // Taking counter payment on an order that already started an online payment risks charging the
  // customer twice (their retry route may still be open). Confirm once — not a PIN — before the
  // payment sheet (H-4c). A plain counter order (no prior online attempt) opens it directly.
  const requestPayment = () => {
    if (order.online_payment_failed && !window.confirm(
      "This customer started an online payment that did not complete. Check with them before taking cash.")) return;
    onPayment(order);
  };

  const handleCancel = async () => {
    if (!window.confirm(`Cancel Order #${order.order_number}? This cannot be undone.`)) return;
    setActing(true);
    try {
      const { data } = await api.delete(`/orders/${order.id}`);
      onCancel(order.id);
      // Surface the refund outcome for a prepaid online order (incl. the 5–7 day
      // note), so staff aren't left guessing and a refund failure is loud.
      if (data?.refund?.message) {
        if (data.refund.status === "failed") toast.error(data.refund.message);
        else toast.success(data.refund.message);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to cancel");
    } finally {
      setActing(false);
    }
  };

  // Print & KOT (AH-4): one press → the kitchen docket first, then the customer bill, in that order —
  // kitchen before customer, matching what the kitchen needs and what Vinod watched Petpooja do.
  // SEQUENCED, never parallel: printKOT resolves only after its own win.print() returns, so we await
  // it before starting the bill — two jobs racing into one thermal spooler is how a docket goes
  // missing. If the KOT fails we do NOT print the bill and stop: a bill without its KOT means the
  // customer is charged for food the kitchen never saw, which is worse than printing neither. (printKOT
  // already toasts its own failure; this makes the consequence — no bill — explicit.)
  const printAndKot = async () => {
    const kotOk = await printKOT(order, venueName, printWidthMm);
    if (!kotOk) {
      toast.error("Bill not printed — the kitchen docket (KOT) must print first. Fix the printer and try again.");
      return;
    }
    await printDocket(order);
  };

  return (
    <div className={`bg-[#1A1A1A] border ${urgencyBorder} rounded-xl flex flex-col transition-colors`}>

      {/* ── Header — flex-none, always visible ── */}
      <div className="flex-none px-3 pt-3 pb-2">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white font-bold text-sm">#{order.order_number}</span>
            {order.order_type === "room" ? (
              <span className="text-xs bg-sky-500/15 text-sky-300 border border-sky-500/30 px-2 py-0.5 rounded-full">
                Room {order.room_number}
              </span>
            ) : order.order_type === "dine_in" ? (
              <span className="text-xs bg-[#9D00FF]/15 text-[#D78CFF] border border-[#9D00FF]/30 px-2 py-0.5 rounded-full">
                Table {order.table_number}
              </span>
            ) : (
              <span className="text-xs bg-white/5 text-[#A0A0A0] border border-white/10 px-2 py-0.5 rounded-full">
                Takeaway
              </span>
            )}
            {order.vehicle_number && (
              <span className="text-xs bg-amber-500/15 text-amber-300 border border-amber-500/30 px-2 py-0.5 rounded-full font-mono">
                🚗 {order.vehicle_number}
              </span>
            )}
            {order.payment_captured && order.status !== "collected" && (
              <span className="text-xs bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 px-2 py-0.5 rounded-full flex items-center gap-1">
                <CheckCircle2 size={9} /> Paid
              </span>
            )}
            {order.payment_status === "paid" && order.payment_method === "online" && (
              <span className="text-xs bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 px-2 py-0.5 rounded-full flex items-center gap-1">
                <CheckCircle2 size={9} /> Paid online
              </span>
            )}
          </div>
          <span className={`text-xs font-mono flex items-center gap-1 shrink-0 ${timerColor}`}>
            <Clock size={11} /> {time}
          </span>
        </div>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-medium text-white leading-tight">{order.customer_name}</p>
            {order.customer_phone && (
              <p className="text-xs text-[#555] mt-0.5">{order.customer_phone}</p>
            )}
            {order.payment_instrument?.detail && (
              <p className="text-[11px] text-emerald-300/70 mt-0.5 truncate" title={order.payment_instrument.detail}>
                💳 {order.payment_instrument.detail}
              </p>
            )}
          </div>
          <span className="text-xs text-[#555] shrink-0">{formatTime(order.created_at)}</span>
        </div>
      </div>

      {/* ── Items — capped at 120px, scrolls within card ── */}
      <div
        className="border-t border-white/5 overflow-y-auto max-h-[120px] md:max-h-[280px]"
      >
        <div className="px-3 py-1.5">
          {order.items.map((it, i) => {
            const details = orderLineDetails(it);
            return (
            <div key={i} className="flex items-baseline justify-between py-0.5">
              <div className="min-w-0">
                <div className="flex items-baseline gap-1.5 min-w-0">
                  <span className="text-[#9D00FF] font-bold text-xs shrink-0">{it.qty}×</span>
                  <span className="text-xs text-white truncate">{it.name}</span>
                  {it.note && (
                    <span className="text-xs text-amber-400 shrink-0">· {it.note}</span>
                  )}
                </div>
                {details && <div className="text-[11px] text-[#B0B0B0] truncate pl-5">{details}</div>}
              </div>
              <span className="text-xs text-[#555] ml-2 shrink-0">
                ₹{(it.line_total || 0).toFixed(0)}
              </span>
            </div>
            );
          })}
          {order.special_note && (
            <div className="flex items-start gap-1.5 bg-amber-500/10 border border-amber-500/20 rounded px-2 py-1.5 mt-1">
              <AlertTriangle size={10} className="text-amber-400 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-300 leading-tight">{order.special_note}</p>
            </div>
          )}
          {/* Online payment failed (H-4b) — this is NOT a plain counter order; the customer tried
              to pay online and it didn't go through. Same amber language as the note box. */}
          {order.online_payment_failed && order.payment_status !== "paid" && (
            <div className="flex items-center gap-1.5 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1.5 mt-1">
              <AlertTriangle size={10} className="text-amber-400 shrink-0" />
              <p className="text-xs font-semibold text-amber-300 leading-tight">Online payment failed</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Total row ── */}
      <div className="flex items-center justify-between px-3 py-1.5 border-t border-white/5">
        <span className="text-xs text-[#555]">
          {order.items.length} item{order.items.length !== 1 ? "s" : ""}
          {order.items.length > 5 && (
            <span className="text-[#444] ml-1">· scroll to see all</span>
          )}
        </span>
        <span className="text-sm font-bold text-white">₹{(order.total || 0).toFixed(0)}</span>
      </div>

      {/* ── CTAs — flex-none, always pinned to card bottom ── */}
      <div className="flex-none border-t border-white/5 px-2.5 py-2 flex items-center gap-2">
        {/* KOT print */}
        <button
          type="button"
          onClick={() => printKOT(order, venueName, printWidthMm)}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-white/10 text-[#A0A0A0] hover:border-white/30 hover:text-white text-xs transition"
        >
          <Printer size={12} /> KOT
        </button>

        {/* Print & KOT (AH-4) — kitchen docket then bill, one press. Kept alongside the separate KOT
            and Docket buttons: reprinting just one stays a real need. */}
        <button
          type="button"
          onClick={printAndKot}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#9D00FF]/20 border border-[#9D00FF]/40 text-[#C77DFF] hover:bg-[#9D00FF]/30 text-xs font-semibold transition"
        >
          <Printer size={12} /> Print &amp; KOT
        </button>

        {/* Status CTAs */}
        {order.status === "pending" && (
          <>
            <button
              type="button"
              disabled={acting}
              onClick={() => move("preparing")}
              className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-amber-500/20 border border-amber-500/40 text-amber-300 text-xs font-semibold hover:bg-amber-500/30 disabled:opacity-50 transition"
            >
              <ChefHat size={12} /> Start
            </button>
            {/* Edit — pending orders only */}
            <button
              type="button"
              disabled={acting}
              onClick={() => onEdit(order)}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-blue-500/30 text-blue-400 hover:bg-blue-500/10 text-xs font-semibold disabled:opacity-50 transition"
            >
              <Pencil size={12} /> Edit
            </button>
            {!order.payment_captured && order.payment_status !== "paid" && order.order_type !== "room" && (
              <button
                type="button"
                disabled={acting}
                onClick={requestPayment}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-xs font-semibold disabled:opacity-50 transition"
              >
                <CreditCard size={12} /> Pay
              </button>
            )}
            <button
              type="button"
              disabled={acting}
              onClick={handleCancel}
              className="flex items-center justify-center px-2.5 py-1.5 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs disabled:opacity-50 transition"
            >
              <XCircle size={13} />
            </button>
          </>
        )}

        {order.status === "preparing" && (
          <>
            <button
              type="button"
              disabled={acting}
              onClick={() => move("ready")}
              className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-emerald-600/20 border border-emerald-500/40 text-emerald-300 text-xs font-semibold hover:bg-emerald-600/30 disabled:opacity-50 transition"
            >
              <CheckCircle2 size={12} /> Mark Ready
            </button>
            {!order.payment_captured && order.payment_status !== "paid" && order.order_type !== "room" && (
              <button
                type="button"
                disabled={acting}
                onClick={requestPayment}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-xs font-semibold disabled:opacity-50 transition"
              >
                <CreditCard size={12} /> Pay
              </button>
            )}
            <button
              type="button"
              disabled={acting}
              onClick={handleCancel}
              className="flex items-center justify-center px-2.5 py-1.5 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs disabled:opacity-50 transition"
            >
              <XCircle size={13} />
            </button>
          </>
        )}

        {order.status === "ready" && (
          order.order_type === "room" ? (
            /* Room orders: deliver to room, settle later via Room Bills */
            <>
              <button
                type="button"
                onClick={() => printDocket(order)}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-white/10 text-[#A0A0A0] hover:border-white/30 hover:text-white text-xs transition"
              >
                <Receipt size={12} /> Docket
              </button>
              <button
                type="button"
                disabled={acting}
                onClick={() => move("delivered")}
                className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-sky-600/20 border border-sky-500/40 text-sky-300 text-xs font-semibold hover:bg-sky-600/30 disabled:opacity-50 transition"
              >
                <Truck size={12} /> Mark Delivered
              </button>
            </>
          ) : (order.payment_captured || order.payment_status === "paid") ? (
            <button
              type="button"
              disabled={acting}
              onClick={() => move("collected")}
              className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-emerald-600/20 border border-emerald-500/40 text-emerald-300 text-xs font-semibold hover:bg-emerald-600/30 disabled:opacity-50 transition"
            >
              <CheckCircle2 size={12} /> Mark Collected
            </button>
          ) : (
            <button
              type="button"
              disabled={acting}
              onClick={requestPayment}
              className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-[#9D00FF]/20 border border-[#9D00FF]/40 text-[#D78CFF] text-xs font-semibold hover:bg-[#9D00FF]/30 disabled:opacity-50 transition"
            >
              <CreditCard size={12} /> Collect & Pay
            </button>
          )
        )}
      </div>
    </div>
  );
}

// ── Column ────────────────────────────────────────────────────────────────────

function Column({ title, icon: Icon, color, orders, badge, ...cardProps }) {
  return (
    <div className="flex flex-col min-w-0 min-h-0 flex-1">
      <div className="flex-none flex items-center gap-2 px-4 py-3 border-b border-white/5">
        <Icon size={15} className={color} />
        <span className="text-sm font-semibold text-white">{title}</span>
        {badge > 0 && (
          <span className={`ml-auto text-xs font-bold px-2 py-0.5 rounded-full ${
            title === "New"       ? "bg-amber-500/20 text-amber-300" :
            title === "Preparing" ? "bg-blue-500/20 text-blue-300" :
                                    "bg-emerald-500/20 text-emerald-300"
          }`}>
            {badge}
          </span>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-3 min-h-0">
        {orders.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-[#A0A0A0] text-sm">
            No orders
          </div>
        ) : (
          orders.map(o => <OrderCard key={o.id} order={o} {...cardProps} />)
        )}
      </div>
    </div>
  );
}

// ── Room Settle Modal ─────────────────────────────────────────────────────────
// Reuses the payment-method picker to settle ALL of a room's delivered orders
// with one shared method. Grouped settle assumes exact payment (no cash change).

function RoomSettleModal({ room, onClose, onSettled }) {
  const [method, setMethod] = useState("cash");
  const [reference, setReference] = useState("");
  const [saving, setSaving] = useState(false);

  const handleMethodChange = (key) => {
    setMethod(key);
    setReference("");
  };

  const refLabel = {
    upi: "UPI Transaction ID (UTR)",
    card: "Approval code / Last 4 digits",
    dineout: "Dineout Booking ID",
    district: "District Booking ID",
    eazydiner: "EazyDiner Booking ID",
  }[method] || "Reference";

  const refPlaceholder = {
    upi: "e.g. 324156789012",
    card: "e.g. AUTH123 or 4242",
    dineout: "e.g. DIN-2026-884521",
    district: "e.g. DST-77421",
    eazydiner: "e.g. ED-19928",
  }[method] || "Reference number";

  const submit = async () => {
    if (!method) return;
    setSaving(true);
    try {
      const { data } = await api.post(
        `/orders/room-bills/${encodeURIComponent(room.room_number)}/settle`,
        {
          payment_method: method,
          ...(reference.trim() ? { payment_reference: reference.trim() } : {}),
        }
      );
      onSettled(room.room_number, data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Settlement failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl w-full max-w-sm p-6">
        <h3 className="text-lg font-bold text-white mb-1">Settle Room {room.room_number}</h3>
        <p className="text-sm text-[#A0A0A0] mb-5">
          {room.order_count} order{room.order_count !== 1 ? "s" : ""} · <span className="text-white font-semibold">₹{(room.combined_total || 0).toFixed(0)}</span>
        </p>

        {/* Payment method */}
        <p className="text-xs uppercase tracking-wider text-[#A0A0A0] mb-2">Payment method</p>
        <div className="grid grid-cols-3 gap-2 mb-4">
          {[
            { key: "cash",      label: "Cash",      icon: Banknote },
            { key: "upi",       label: "UPI",       icon: Smartphone },
            { key: "card",      label: "Card",      icon: CardIcon },
            { key: "dineout",   label: "Dineout",   icon: CreditCard },
            { key: "district",  label: "District",  icon: CreditCard },
            { key: "eazydiner", label: "EazyDiner", icon: CreditCard },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => handleMethodChange(key)}
              className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border text-xs font-semibold transition ${
                method === key
                  ? "bg-[#9D00FF]/20 border-[#9D00FF] text-white"
                  : "border-white/10 text-[#A0A0A0] hover:border-white/30"
              }`}
            >
              <Icon size={18} />
              {label}
            </button>
          ))}
        </div>

        {/* Non-cash: reference number */}
        {method !== "cash" && (
          <div className="mb-4">
            <label className="text-xs uppercase tracking-wider text-[#A0A0A0] block mb-2">
              {refLabel}
              <span className="text-[#555] normal-case tracking-normal ml-1">(optional)</span>
            </label>
            <input
              type="text"
              value={reference}
              onChange={e => setReference(e.target.value)}
              placeholder={refPlaceholder}
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:border-[#9D00FF] focus:outline-none"
              style={{ fontSize: 16 }}
              autoFocus
            />
          </div>
        )}

        <p className="text-[11px] text-[#555] mb-4">
          Each order keeps its own invoice and earns loyalty separately. No WhatsApp is sent for room orders.
        </p>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 rounded-xl border border-white/10 text-[#A0A0A0] text-sm font-semibold hover:border-white/30 transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="flex-1 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-semibold transition"
          >
            {saving ? "Settling…" : "Settle All"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Room Bills Panel ──────────────────────────────────────────────────────────
// Staff-facing view of delivered-but-unsettled room orders, grouped by room.
// "Settle All" applies the per-order settlement to each with one method.

function RoomBillsPanel() {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // {room_number: bool}
  const [settleRoom, setSettleRoom] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/orders/room-bills");
      setRooms(data);
    } catch (_) {
      toast.error("Failed to load room bills");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (rn) => setExpanded(e => ({ ...e, [rn]: !e[rn] }));

  const handleSettled = (roomNumber, res) => {
    setSettleRoom(null);
    setRooms(prev => prev.filter(r => r.room_number !== roomNumber));
    toast.success(`Room ${roomNumber} settled — ${res.order_count} order${res.order_count !== 1 ? "s" : ""}, ₹${Math.round(res.combined_total || 0)}`);
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto p-3 md:p-4">
      <div className="flex items-center gap-3 mb-3">
        <span className="text-xs text-[#444]">
          {rooms.length} room{rooms.length !== 1 ? "s" : ""} with unsettled orders
        </span>
        <button
          type="button"
          onClick={() => load()}
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 text-[#A0A0A0] hover:text-white hover:border-white/30 text-xs transition"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-[#A0A0A0] text-sm">Loading room bills…</div>
      ) : rooms.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <BedDouble size={40} className="text-[#333] mb-3" />
          <p className="text-[#A0A0A0] text-sm">No delivered orders awaiting settlement.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3 max-w-3xl mx-auto">
          {rooms.map(room => {
            const isOpen = !!expanded[room.room_number];
            return (
              <div key={room.room_number} className="bg-[#1A1A1A] border border-white/10 rounded-xl overflow-hidden">
                {/* Header */}
                <div className="flex items-center gap-3 px-4 py-3">
                  <button
                    type="button"
                    onClick={() => toggle(room.room_number)}
                    className="flex items-center gap-2 min-w-0 flex-1 text-left"
                  >
                    {isOpen ? <ChevronDown size={16} className="text-[#A0A0A0] shrink-0" /> : <ChevronRight size={16} className="text-[#A0A0A0] shrink-0" />}
                    <span className="flex items-center gap-1.5 text-xs bg-sky-500/15 text-sky-300 border border-sky-500/30 px-2 py-0.5 rounded-full shrink-0">
                      <BedDouble size={11} /> Room {room.room_number}
                    </span>
                    <span className="text-xs text-[#A0A0A0] truncate">
                      {room.order_count} order{room.order_count !== 1 ? "s" : ""}
                    </span>
                  </button>
                  <span className="text-sm font-bold text-white shrink-0">₹{(room.combined_total || 0).toFixed(0)}</span>
                  <button
                    type="button"
                    onClick={() => setSettleRoom(room)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-600/30 text-xs font-semibold transition shrink-0"
                  >
                    <CreditCard size={12} /> Settle All
                  </button>
                </div>

                {/* Expanded — itemized orders */}
                {isOpen && (
                  <div className="border-t border-white/5 divide-y divide-white/5">
                    {room.orders.map(o => (
                      <div key={o.id} className="px-4 py-2.5">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-white font-bold text-xs">#{o.order_number}</span>
                            <span className="text-xs text-[#555]">{formatTime(o.created_at)}</span>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-sm font-semibold text-white">₹{(o.total || 0).toFixed(0)}</span>
                            <button
                              type="button"
                              onClick={() => printDocket(o)}
                              className="flex items-center gap-1 px-2 py-1 rounded-lg border border-white/10 text-[#A0A0A0] hover:border-white/30 hover:text-white text-xs transition"
                            >
                              <Receipt size={11} /> Docket
                            </button>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                          {o.items.map((it, i) => (
                            <span key={i} className="text-xs text-[#A0A0A0]">
                              <span className="text-[#9D00FF] font-bold">{it.qty}×</span> {it.name}
                              {orderLineDetails(it) && <span className="text-[#6B6B6B]"> ({orderLineDetails(it)})</span>}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {settleRoom && (
        <RoomSettleModal
          room={settleRoom}
          onClose={() => setSettleRoom(null)}
          onSettled={handleSettled}
        />
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function Orders() {
  const { user, hasFeature } = useAuth();
  // Orders, loading and the last-fetch time come from the shell-level watcher (AppLayout),
  // so the board no longer owns a poll — it shares the state that also drives the nav badge
  // and alerts, and it can't be blocked on load by audio (the old chime lived here and hung
  // the board — see the bug fix note in the shell). Optimistic edits go through
  // orderAlerts.mutate; a manual refresh / post-mutation refresh through orderAlerts.refetch.
  const { orders, loading, lastFetch, connected } = useOrderAlerts();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [paymentOrder, setPaymentOrder] = useState(null);
  const [newOrderOpen, setNewOrderOpen] = useState(false);
  // "Awaiting payment" strip (H-3): pay-online orders with no money arrived yet. Board-local,
  // read-only; they are deliberately NOT in the shared `orders` (gated off the board + the alarm).
  const [awaiting, setAwaiting] = useState([]);
  const [awaitingOpen, setAwaitingOpen] = useState(false);
  const [editOrder, setEditOrder] = useState(null); // order object being edited
  const [mobileTab, setMobileTab] = useState("pending");
  const [boardView, setBoardView] = useState("board"); // "board" | "rooms"
  const [navTop, setNavTop] = useState(57);
  // Notification opt-in banner. The browser suppresses a permission prompt without user
  // activation, so it must come from a click (Part 4). Show it only when a prompt can still
  // succeed (permission still "default") and the guest hasn't dismissed it on this device.
  const [notifyBannerOpen, setNotifyBannerOpen] = useState(() => {
    try {
      return typeof window !== "undefined" && "Notification" in window
        && Notification.permission === "default"
        && localStorage.getItem("tabon-notify-banner-dismissed") !== "1";
    } catch (_) { return false; }
  });

  const venueName = user?.venue?.name || "Venue";
  const isOrdering = hasFeature("ordering");
  const venueKey = user?.venue?.id || user?.venue?.venue_code || user?.venue_id || null;
  const roomServiceEnabled = user?.venue?.room_service_enabled === true;

  // Ensure the shared watcher is running even if the board is opened directly (idempotent —
  // AppLayout also starts it). No teardown here: the watcher persists across navigation.
  useEffect(() => {
    if (isOrdering && venueKey) orderAlerts.ensureStarted(venueKey);
  }, [isOrdering, venueKey]);

  // "New Order from anywhere" (Commit 5): the chrome navigates here with ?new=1. Open the modal,
  // then strip the param so a refresh or Back does not reopen it.
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setNewOrderOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Awaiting-payment orders (H-3) — fetched board-locally and refreshed on the shared watcher's
  // cadence (lastFetch ticks on every order event / poll), so the strip tracks reality without a
  // new poll of its own. A transient error keeps the last list rather than blanking the strip.
  const loadAwaiting = useCallback(async () => {
    try {
      const { data } = await api.get("/orders/awaiting-payment");
      setAwaiting(Array.isArray(data) ? data : []);
    } catch (_) { /* keep the last list */ }
  }, []);
  useEffect(() => { if (isOrdering) loadAwaiting(); }, [isOrdering, loadAwaiting, lastFetch]);
  const cancelAwaiting = useCallback(async (order) => {
    if (!window.confirm(`Cancel Order #${order.order_number}? The customer hasn't paid.`)) return;
    try { await api.delete(`/orders/${order.id}`); } catch (_) {}
    loadAwaiting();
    orderAlerts.refetch();
  }, [loadAwaiting]);

  // The click IS the user activation the browser requires (and informed consent). There's
  // one clean attempt per device — a dismissed prompt blocks the origin until the user
  // resets it in browser settings — so hide the banner after either outcome.
  const enableNotifications = useCallback(() => {
    orderAlerts.requestNotifyPermission();
    setNotifyBannerOpen(false);
  }, []);
  const dismissNotifyBanner = useCallback(() => {
    try { localStorage.setItem("tabon-notify-banner-dismissed", "1"); } catch (_) {}
    setNotifyBannerOpen(false);
  }, []);

  useEffect(() => {
    const measure = () => {
      const header = document.querySelector("header");
      if (header) setNavTop(header.getBoundingClientRect().bottom);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const handleStatusChange = (orderId, newStatus) => {
    // Optimistic update on the SHARED state (reflects on the board and the nav badge).
    // 'collected' and 'delivered' both leave the live board (delivered room orders move to
    // the Room Bills view for grouped settlement).
    if (newStatus === "collected" || newStatus === "delivered") {
      orderAlerts.mutate(prev => prev.filter(o => o.id !== orderId));
    } else {
      orderAlerts.mutate(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
    }
  };

  const handleCancel = (orderId) => {
    orderAlerts.mutate(prev => prev.filter(o => o.id !== orderId));
  };

  const handlePaid = () => {
    setPaymentOrder(null);
    orderAlerts.refetch();
  };

  const handleOrderPlaced = () => orderAlerts.refetch();
  const handleOrderUpdated = () => orderAlerts.refetch();
  const handleEdit = (order) => setEditOrder(order);

  if (!isOrdering) {
    return (
      <AppLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-6">
          <ClipboardList size={48} className="text-[#A0A0A0] mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Ordering not enabled</h2>
          <p className="text-[#A0A0A0] text-sm max-w-xs">
            QR Ordering & POS is not enabled for this venue. Contact your TabOn account manager to get started.
          </p>
        </div>
      </AppLayout>
    );
  }

  const pending   = orders.filter(o => o.status === "pending");
  const preparing = orders.filter(o => o.status === "preparing");
  const ready     = orders.filter(o => o.status === "ready");

  const cardProps = {
    onStatusChange: handleStatusChange,
    onPayment: setPaymentOrder,
    onCancel: handleCancel,
    onEdit: handleEdit,
    venueName,
    printWidthMm: user?.venue?.receipt_width_mm || 80,
  };

  return (
    <>
      {/* Nav only — content lives in the fixed board below */}
      <AppLayout><div /></AppLayout>

      {/* Fixed full-viewport KOT board, sits below nav (z-40) */}
      <div
        style={{
          position: "fixed",
          top: navTop,
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 30,
          display: "flex",
          flexDirection: "column",
          background: "#0A0A0A",
        }}
        className="pb-16 md:pb-0"
      >
        {/* Slim top bar — operational status only */}
        <div className="flex-none flex items-center gap-3 px-4 py-2 border-b border-white/5">
          {boardView === "board" && (
            <>
              <span className="text-xs flex items-center gap-1.5" style={{ color: connected ? "#4ADE80" : "#A0A0A0" }}>
                <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: connected ? "#4ADE80" : "#A0A0A0" }} />
                {connected ? "Live" : "Reconnecting…"}
              </span>
              {lastFetch && (
                <span className="text-xs text-[#333]">
                  {lastFetch.toLocaleTimeString("en-IN", {
                    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
                  })}
                </span>
              )}
            </>
          )}
          {roomServiceEnabled && (
            <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-lg p-0.5">
              <button
                type="button"
                onClick={() => setBoardView("board")}
                className={`px-3 py-1 rounded-md text-xs font-semibold transition ${boardView === "board" ? "bg-[#9D00FF]/30 text-white" : "text-[#A0A0A0] hover:text-white"}`}
              >
                Board
              </button>
              <button
                type="button"
                onClick={() => setBoardView("rooms")}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold transition ${boardView === "rooms" ? "bg-[#9D00FF]/30 text-white" : "text-[#A0A0A0] hover:text-white"}`}
              >
                <BedDouble size={12} /> Room Bills
              </button>
            </div>
          )}
          {boardView === "board" && (
            <div className="ml-auto flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setNewOrderOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#9D00FF]/20 border border-[#9D00FF]/40 text-[#D78CFF] hover:bg-[#9D00FF]/30 text-xs font-semibold transition"
              >
                <Plus size={12} /> New Order
              </button>
              <button
                type="button"
                onClick={() => orderAlerts.refetch()}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 text-[#A0A0A0] hover:text-white hover:border-white/30 text-xs transition"
              >
                <RefreshCw size={12} /> Refresh
              </button>
            </div>
          )}
        </div>

        {/* Dine-in signpost (Commit 5; reworded T Commit 2) — a standalone dine-in order DOES appear
            on this board (Prompt S Commit 0), so the banner must not claim the board excludes dine-in;
            it points at Tables for bills with a RUNNING TAB, the distinction that matters when hunting
            a table's bill. Gated exactly like the Tables nav link. */}
        {boardView === "board" && isOrdering && ["dine_in", "both"].includes(user?.venue?.ordering_mode) && (
          <div className="flex-none border-b border-white/5 px-4 py-2 text-xs text-[#A0A0A0]" style={{ background: "rgba(157,0,255,0.05)" }}>
            Managing a table's running bill? It lives in{" "}
            <button type="button" onClick={() => navigate("/tables")} className="text-[#D78CFF] font-semibold hover:underline">Tables</button>.
          </div>
        )}

        {/* Awaiting payment (H-3) — pay-online orders where no money has arrived yet. A thin,
            collapsed strip so staff can answer "where is my order"; read-only, Cancel the only
            action. These never reach a column, the kitchen, revenue, points or a message. */}
        {boardView === "board" && awaiting.length > 0 && (
          <div className="flex-none border-b border-white/5" style={{ background: "rgba(251,191,36,0.06)" }}>
            <button
              type="button"
              onClick={() => setAwaitingOpen((v) => !v)}
              className="w-full flex items-center gap-2 px-4 py-2 text-left"
            >
              {awaitingOpen ? <ChevronDown size={14} className="text-amber-300" /> : <ChevronRight size={14} className="text-amber-300" />}
              <span className="text-xs font-semibold text-amber-300">Awaiting payment ({awaiting.length})</span>
              {/* Distinguish a declined card from a customer still on the payment page (I-6b). */}
              {(() => {
                const declined = awaiting.filter((o) => o.online_payment_failed).length;
                return (
                  <span className="text-[11px] text-[#777] hidden sm:inline">
                    {declined > 0
                      ? `${declined} declined · ${awaiting.length - declined} awaiting`
                      : "online checkout started — no payment yet"}
                  </span>
                );
              })()}
            </button>
            {awaitingOpen && (
              <div className="px-4 pb-2 flex flex-col">
                {awaiting.map((o) => {
                  const failed = !!o.online_payment_failed;
                  const amt = (o.bill && o.bill.rounded_total != null) ? o.bill.rounded_total : Math.round(o.total || 0);
                  const lastAt = o.last_payment_attempt_at
                    ? new Date(o.last_payment_attempt_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
                    : null;
                  const attempts = Number(o.payment_attempt_count) || 0;
                  return (
                  <div key={o.id} className="py-1.5 border-t border-white/5">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-bold text-white shrink-0">#{o.order_number}</span>
                      <span className="text-[#A0A0A0] truncate min-w-0">{o.customer_name || "—"}</span>
                      {failed && (
                        <span className="shrink-0 text-[9px] font-bold uppercase tracking-wide bg-amber-500/20 text-amber-300 border border-amber-500/40 px-1.5 py-0.5 rounded">
                          Online payment failed
                        </span>
                      )}
                      <span className="ml-auto text-[#A0A0A0] shrink-0">₹{amt}</span>
                      <button
                        type="button"
                        onClick={() => cancelAwaiting(o)}
                        className="shrink-0 flex items-center gap-1 px-2 py-1 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 font-semibold"
                      >
                        <XCircle size={12} /> Cancel
                      </button>
                    </div>
                    {failed && (
                      <div className="text-[11px] text-[#777] mt-0.5 truncate">
                        {attempts > 0 ? `${attempts} attempt${attempts > 1 ? "s" : ""}` : "Declined"}
                        {lastAt ? ` · last ${lastAt}` : ""}
                        {o.last_payment_failure_reason ? ` · ${o.last_payment_failure_reason}` : ""}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Desktop-alerts opt-in — the Enable click is the user activation the browser
            requires for the permission prompt, and informed consent. Dismissal is per-device. */}
        {isOrdering && notifyBannerOpen && (
          <div
            className="flex-none flex items-center justify-between gap-3 px-4 py-2 border-b border-white/5"
            style={{ background: "rgba(157,0,255,0.08)" }}
          >
            <span className="text-xs" style={{ color: "#D78CFF" }}>
              Enable desktop alerts so you're notified when you're in another app.
            </span>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                data-testid="notify-enable"
                onClick={enableNotifications}
                className="px-3 py-1 rounded-lg text-xs font-bold text-white"
                style={{ background: "#9D00FF" }}
              >
                Enable
              </button>
              <button
                type="button"
                data-testid="notify-dismiss"
                onClick={dismissNotifyBanner}
                aria-label="Dismiss"
                className="text-[#A0A0A0] hover:text-white"
              >
                <XCircle size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Room Bills view — delivered room orders grouped for settlement */}
        {boardView === "rooms" ? (
          <RoomBillsPanel />
        ) : /* KOT board — 3 columns each independently scrolling */
        loading ? (
          <div className="flex-1 flex items-center justify-center text-[#A0A0A0]">
            Loading orders…
          </div>
        ) : (
          <>
            {/* ── Mobile (<768px): tab switcher, one column at a time ── */}
            <div className="md:hidden flex-1 flex flex-col min-h-0 overflow-hidden">
              {/* Tab bar */}
              <div className="flex-none flex border-b border-white/5">
                {[
                  { key: "pending",   label: "New",      count: pending.length,   color: "#fbbf24" },
                  { key: "preparing", label: "Preparing", count: preparing.length, color: "#60a5fa" },
                  { key: "ready",     label: "Ready",     count: ready.length,     color: "#34d399" },
                ].map(tab => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setMobileTab(tab.key)}
                    style={{
                      borderBottom: mobileTab === tab.key ? `2px solid ${tab.color}` : "2px solid transparent",
                      color: mobileTab === tab.key ? tab.color : "#555",
                    }}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold transition"
                  >
                    {tab.label}
                    {tab.count > 0 && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-white/10">
                        {tab.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>
              {/* Active column cards */}
              <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-3 min-h-0">
                {(mobileTab === "pending" ? pending : mobileTab === "preparing" ? preparing : ready).length === 0
                  ? <div className="flex items-center justify-center py-16 text-[#A0A0A0] text-sm">No orders</div>
                  : (mobileTab === "pending" ? pending : mobileTab === "preparing" ? preparing : ready).map(o => <OrderCard key={o.id} order={o} {...cardProps} />)
                }
              </div>
            </div>
            {/* ── Desktop (≥768px): 3-column grid ── */}
            <div className="hidden md:flex flex-1 min-h-0 overflow-hidden divide-x divide-white/5">
              <Column title="New"       icon={ClipboardList} color="text-amber-400"   orders={pending}   badge={pending.length}   {...cardProps} />
              <Column title="Preparing" icon={ChefHat}       color="text-blue-400"    orders={preparing} badge={preparing.length} {...cardProps} />
              <Column title="Ready"     icon={CheckCircle2}  color="text-emerald-400" orders={ready}     badge={ready.length}     {...cardProps} />
            </div>
          </>
        )}
      </div>

      {/* Payment modal */}
      {paymentOrder && (
        <PaymentModal
          order={paymentOrder}
          onClose={() => setPaymentOrder(null)}
          onPaid={handlePaid}
        />
      )}

      {/* New counter order modal */}
      {newOrderOpen && (
        <NewOrderModal
          venue={user?.venue}
          onClose={() => setNewOrderOpen(false)}
          onOrderPlaced={handleOrderPlaced}
        />
      )}

      {/* Edit order modal — pending orders only */}
      {editOrder && (
        <EditOrderModal
          order={editOrder}
          onClose={() => setEditOrder(null)}
          onUpdated={handleOrderUpdated}
        />
      )}
    </>
  );
}
